/**
 * The workspace (part 11 log, tables C, L, B and principles W1–W6): one tab's connection to the local store, its edit
 * session, the books, the open book's chapters and the one open chapter. Every change the writer asks for runs one at a
 * time, after the open chapter's typing is saved where it must be (part 08 notes). Everything with an effect is
 * injected: the store, Web Locks, the channel, the clock, IDs and the editor itself, so the logic runs in tests
 * without a browser.
 */
import { alignTexts, ChapterPlaces, isLongChapter, orderChapters, ScanBudget, splitPoints } from '@cockatrice/core';
import type { Alignment, ImageReference, ParsedChapter, SplitPoint, SplitProblem } from '@cockatrice/core';
import type { AutosaveResult, AutosaveStatus, NoteMark, ParagraphLook, TextRange } from '@cockatrice/editor';
import {
  DEFAULT_THEME,
  METADATA_TEXT,
  metadataFromJson,
  newMetadata,
  normalizeText,
  parseMetadata,
  serializeMetadata,
  serializeTheme,
  themeFromJson,
} from '@cockatrice/layout';
import type { BookLanguage, BookMetadata, PdfBook, Theme } from '@cockatrice/layout';
import { abandonPending, BundleImport, ChapterBook, EditSession, exportPinUntil, ImageLibrary, LocksUnsupportedError, Notes, pendingBooks, SnapshotHistory, StoreOutdatedError, targetKey } from '@cockatrice/storage';
import type {
  BookListing,
  BookRecord,
  BeginImportResult,
  Channel,
  ChapterEntry,
  ApplyPulledResult,
  ChapterText,
  ConflictCopy,
  EncodedImage,
  ImageAddOutcome,
  Locks,
  NewNote,
  NoteAnchor,
  NoteChangeResult,
  NoteEdit,
  NoteListing,
  NoteRecord,
  NoteTarget,
  ReplacedText,
  SessionState,
  CreateResult,
  ShareLinkRecord,
  ShareRecord,
  Store,
  StoreFailure,
  UpdateRequest,
  UpdateResult,
  WriteOp,
} from '@cockatrice/storage';
import { batch, createSignal } from 'solid-js';
import type { Accessor, Setter } from 'solid-js';
import type { BundleRequest } from '../bundle/controller.ts';
import type { CommitRequest, CommitResult } from '../import/controller.ts';
import type { FindChapter, ReplacedChapter } from '../find/protocol.ts';
import { bookForExport, readForExport } from './book-export.ts';
import type { BookSource, ExportedBook } from './book-export.ts';
import type { DocxBook } from '../docx/controller.ts';
import { readingCopyOf } from '../share/copy.ts';
import type { ShareCopy } from '../share/copy.ts';
import type { SharedChapter, ShareStore } from '../share/links.ts';
import type { CopyBook } from '../share/copies.ts';
import type { InviteStore } from '../share/invites.ts';
import type { ExportBase, ReimportCommitResult, ReimportRequest, StoredPicture } from '../reimport/reimport.types.ts';
import { bundleRequest } from './bundle-request.ts';
import { settleEditor } from '../pwa/settle.ts';
import type { KeyBindings, Platform } from './keys.ts';
import { canEdit, screenFor } from './screen.ts';
import type { Screen, StoreOpening } from './screen.ts';
import { paragraphLookOf } from './paragraph-look.ts';
import { changeWriting, DEFAULT_SETTINGS, NO_WRITING, parseSettings, rememberChapter, serializeSettings } from './settings.ts';
import type { BookWriting, DeviceSettings } from './settings.ts';
import { COMPOSER_KEY, noteEditOf, readUnsaved, recoveryOf, serializeUnsaved, textHash, UNSAVED_PREFIX } from './unsaved.ts';
import type { Unsaved, UnsavedChapter, UnsavedComposer, UnsavedNote, UnsavedStorage } from './unsaved.ts';

/** An open chapter editor, as the workspace drives it (part 09's `ChapterEditor` in the app). */
export interface EditorHandle {
  /** The text the editor holds now and its tree, which the editor keeps parsed (part 09, E2). */
  readonly parsed: ParsedChapter;
  /** Where the cursor is in the text (the main selection's head), for the preview (part 15). */
  readonly cursor: number;
  readonly status: AutosaveStatus;
  flush(): Promise<AutosaveResult>;
  close(): Promise<AutosaveResult>;
  pause(paused: boolean): void;
  setAutoConversion(on: boolean): void;
  /** Shows the text in `look` from now on (part 10a, S3, S5). */
  setParagraphLook(look: ParagraphLook): void;
  /** Makes the text due a save again (part 09, E9). */
  resave(): void;
  /** Part 16b, I1–I3: keeps a place (the cursor's when not given) where an image will go; its mark's ID. */
  markImage(at?: number): number;
  /** Part 16b, I6, I10: writes an image's markup at a mark; false when it can't (closed, paused, the mark gone). */
  insertImage(mark: number, markup: string): boolean;
  /** Part 16b: forgets a mark. */
  releaseImage(mark: number): void;
  /** Part 19a, F2, F9: shows `ranges` (a flat list of the text's positions) as matches, none current. */
  showMatches(ranges: readonly number[]): void;
  /** Part 19a, F9: the matches shown, mapped through every edit since, as a flat list. */
  readonly matches: readonly number[];
  /** Part 19a, F5: the current match, mapped through every edit since, or null. */
  readonly currentMatch: TextRange | null;
  /** Part 19a, F3: the main selection. */
  readonly selection: TextRange;
  /** Part 19a, F3, F4: makes `range` the current match, selected and scrolled to; the focus stays where it is. */
  selectMatch(range: TextRange): void;
  /** Part 19a, F5: makes the text `text` as one undoable change when it holds `expected`; where it changed, or null. */
  replaceText(expected: string, text: string): TextRange | null;
  /** Part 19e, MK1–MK4: shows `marks` (positions in the text now) as the notes' marks, replacing those shown. */
  showNotes(marks: readonly NoteMark[]): void;
  /** Part 19e, D′11: selects `range` and gives the text the focus. */
  goTo(range: TextRange): void;
}

/** Part 19e, table AR: where the open chapter's notes were found, by note ID (null: detached), in its visible text. */
export interface FoundNotes {
  readonly chapterId: string;
  readonly found: ReadonlyMap<string, TextRange | null>;
}

/** Part 19e, NO1–NO5: a note the writer saves: its words and where it sits; the book is the open one. */
export type NoteDraft = Omit<NewNote, 'bookId'>;

/** Part 19a, M5, M6: the texts a search reads, the chapters in scope in order (the open one as typed), and how many couldn't be read. */
export interface FindTexts {
  readonly chapters: readonly FindChapter[];
  readonly unreadable: number;
}

/** Part 19a, F2: the open chapter alone, or every live chapter of the open book. */
export type FindScope = 'chapter' | 'book';

/** Part 19a, table L: what the find worker made of the chapters a replace-all sent it. */
export type ReplaceComputation =
  | { readonly kind: 'replaced'; readonly chapters: readonly ReplacedChapter[] }
  | { readonly kind: 'stopped' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'refused'; readonly problem: 'too-long' | 'invalid' };

/**
 * Part 19a, table L: what a replace-all did: matches made and left in how many chapters (`chapters` changed), or why
 * nothing was replaced: the worker's (L2), a chapter changed under it (L7), the workspace not editable (F12), the open
 * chapter's save (L1), the lock gone or the store failing (L5, L6).
 */
export type ReplaceAllOutcome =
  | { readonly kind: 'replaced'; readonly made: number; readonly left: number; readonly chapters: number }
  | { readonly kind: 'stopped' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'refused'; readonly problem: 'too-long' | 'invalid' }
  | { readonly kind: 'changed' }
  | { readonly kind: 'not-editable' }
  | { readonly kind: 'save-failed' }
  | { readonly kind: 'stale' }
  | { readonly kind: 'write-failed' };

/** Part 19a, table U: what the book-wide undo did, or why it did nothing. */
export type UndoReplaceOutcome =
  | { readonly kind: 'undone'; readonly restored: number; readonly kept: number }
  | { readonly kind: 'not-editable' }
  | { readonly kind: 'save-failed' }
  | { readonly kind: 'stale' }
  | { readonly kind: 'write-failed' };

/** Part 19a, U7: the last replace-all, which its undo can take back: its book and the chapters it changed. */
interface LastReplace {
  readonly bookId: string;
  readonly chapters: readonly ReplacedText[];
}

/** What opening an editor needs. */
export interface EditorRequest {
  readonly text: string;
  readonly language: BookLanguage;
  readonly autoConversion: boolean;
  /** Part 10a, S4: the paragraph look, the book look from the open book's theme. */
  readonly paragraphLook: ParagraphLook;
  readonly save: (text: string) => Promise<AutosaveResult>;
  readonly onStatus: (status: AutosaveStatus) => void;
}

/** What the workspace needs; everything with an effect is injected. */
export interface WorkspaceOptions {
  /** Opens the local store: IndexedDB in the app, memory in tests. */
  readonly openStore: () => Promise<Store>;
  readonly locks: Locks | undefined;
  /** A new channel every tab of the app opens under one name, for each session. */
  readonly channel: () => Channel;
  readonly delay: (ms: number) => Promise<void>;
  readonly now: () => number;
  readonly newId: () => string;
  /** SHA-256 (WebCrypto's digest): an image's hash (part 16a). */
  readonly digest: (bytes: Uint8Array) => Promise<Uint8Array>;
  /** The time zone offset at a time, in minutes east of UTC. */
  readonly zone: (at: number) => number;
  readonly platform: Platform;
  /** The keys the chapter editor binds (`editorKeys`). */
  readonly reserved: ReadonlySet<string>;
  /** Asks the browser to keep the store (decision 3); never awaited (part 06, P5). */
  readonly persist: () => void;
  /** Opens a chapter editor wherever the interface keeps it. */
  readonly openEditor: (request: EditorRequest) => EditorHandle;
  /** Runs `run` soon, after the current work: the long-chapter check, kept out of the chapter switch (decision 14). */
  readonly soon: (run: () => void) => void;
  /** The title a new book gets, in the interface's language. */
  readonly untitledBook: () => string;
  /** Called each time the book is ready after the session took the lock (start, "use here", a new connection). */
  readonly onReady: () => void;
  /** Fix-next 1, table U: where a page keeps its typing not stored yet as it goes (`localStorage`; getting it may throw). */
  readonly unsavedStorage: () => UnsavedStorage;
  /**
   * Part 36a, table RO: whether the account lets the writer change their books now (the account's `permit`); always
   * when not given (accounts off). Reading, export and storing typing already done never ask it (principle C1).
   */
  readonly permit?: () => boolean;
  /**
   * Part 36a, deep-fix r4, table BK: the names of the store's own edit lock and of its pages' records of typing not stored
   * yet, asked once the store is open; the first store's (`EDIT_LOCK`, `UNSAVED_PREFIX`) when not given.
   */
  readonly space?: () => { readonly editLock: string; readonly unsaved: string };
  /**
   * BK2: whether a store with no book gets a new one at the start (B10, decision 13); always when not given. The empty
   * store shown with nobody's books makes none, so its library shows no book.
   */
  readonly firstBook?: () => boolean;
}

/** A book in the book list (B1): its record's details, or none when its record is missing or unreadable. */
export interface BookSummary {
  readonly id: string;
  /** Null when the record is missing or can't be read: the interface names it, and its chapters still show (B2). */
  readonly metadata: BookMetadata | null;
  /** The book's theme, for the preview (part 15); the default theme when the record has none that can be read. */
  readonly theme: Theme;
  /** Whether a record exists, readable or not; one that exists and can't be read is never overwritten (B3). */
  readonly stored: boolean;
  /** Part 11h: when its record was made (Book details says it), or null with no readable record. */
  readonly createdAt: number | null;
}

/**
 * Part 24c, S1: how a bundle import's storing began: under way, with what it writes through (a storage `BundleImport` of
 * this turn); or why not (`BeginImportResult`'s, or the workspace can't be edited).
 */
export type ImportStart = BeginImportResult | { readonly kind: 'not-editable' };

/**
 * Part 24d, S′5, S′6: how an update's Apply ended: written, or `changed` (U11), or a write's failure (`UpdateResult`); or
 * nothing tried, the workspace not editable or the open chapter's save failing.
 */
export type UpdateCommitResult = UpdateResult | { readonly kind: 'not-editable' } | { readonly kind: 'save-failed' };

/** Something the writer is told (C-rows' outcomes). */
export type Notice =
  | { readonly kind: 'save-failed' }
  | { readonly kind: 'autosave-failed' }
  | { readonly kind: 'write-failed' }
  | { readonly kind: 'copy-kept' }
  | { readonly kind: 'unreadable' }
  | { readonly kind: 'read-failed' }
  | { readonly kind: 'split-refused'; readonly problem: SplitProblem }
  | { readonly kind: 'title-refused' }
  /** Part 19c, N7: notes typed couldn't be stored yet; they are kept and tried again. */
  | { readonly kind: 'notes-failed' }
  /** Part 19c, W5: another tab changed a note this tab typed in; both versions are in it, the other tab's first. */
  | { readonly kind: 'notes-joined' }
  /** Part 19c, W1: notes typed for a chapter or note another tab deleted for good weren't kept. */
  | { readonly kind: 'notes-lost' }
  /** Part 19e, NO11: the note acted on was deleted in another tab. */
  | { readonly kind: 'note-gone' }
  /** Part 19e, CP3, NO12: a new note, a reply, a resolve or a delete couldn't be stored. */
  | { readonly kind: 'note-failed' }
  /** Fix-next 1, M3, M7 (*deep-fix r2*): typing an earlier page kept as it went couldn't be stored yet; it is tried again. */
  | { readonly kind: 'unsaved-waiting' };

/** Typing kept as a conflict copy (part 06, L1; W3, W4), and the chapter it was typed in, or null when that is gone. */
export interface KeptCopy {
  readonly copy: ConflictCopy;
  readonly chapter: ChapterEntry | null;
}

/** A split the writer can make: the text its points belong to, and the points (C5). */
export interface SplitOffer {
  readonly text: string;
  readonly points: readonly SplitPoint[];
}

/** A store connection and its session. */
interface Connection {
  readonly store: Store;
  readonly session: EditSession;
}

/** The chapter open in the editor, and what its saves need (W3), before the editor exists. */
interface SlotState {
  readonly chapterId: string;
  readonly bookId: string;
  /** The stored text this editor's text grew from: what it opened, or what it last saved as the chapter. */
  base: string;
  /** Set when the stored chapter changed under the editor (another tab); its saves then go to conflict copies (W4). */
  diverged: boolean;
  /** Fix-next 1: the text of the save running, which the store may hold once it ends; null with none running. */
  saving: string | null;
  /** The connection saves go through: the newest, which replaces the one the chapter opened on (L5, L6). */
  connection: Connection;
}

/** The chapter open in the editor. */
interface Slot extends SlotState {
  readonly editor: EditorHandle;
}

/** What this device's operations need: its ID, and its history and book on the connection they were read from. */
interface Device {
  readonly kind: 'device';
  readonly id: string;
  readonly history: SnapshotHistory;
  readonly book: ChapterBook;
  /** Part 19e: the open book's notes. */
  readonly notes: Notes;
  readonly connection: Connection;
}

/** Part 38d: what a share store's create answers with no device (the store not open); part 38e: a note's change too. */
const NO_STORE = Object.freeze({ kind: 'failed' as const, error: new Error('the store isn’t open') });

/** Part 19c, N2: how long after the last keystroke in a note its typing is stored. */
export const NOTE_DELAY_MS = 600;

/** A Solid signal as a pair the workspace keeps. */
interface Signal<T> {
  readonly get: Accessor<T>;
  readonly set: Setter<T>;
}

function signal<T>(value: T): Signal<T> {
  const [get, set] = createSignal(value);
  return { get, set };
}

/**
 * A title as stored (B4, B12): cut to part 05's length for titles, then normalised as part 05 does titles (no line
 * breaks, tabs or forbidden characters; a surrogate pair the cut split loses its half), and trimmed.
 */
export function cleanTitle(title: string): string {
  return normalizeText(title.slice(0, METADATA_TEXT.title.max), METADATA_TEXT.title).trim();
}

/** The workspace (tables C, L, B). */
export class Workspace {
  private readonly options: WorkspaceOptions;
  private connection: Connection | null = null;
  private device: Device | null = null;
  private slot: Slot | null = null;
  private queue: Promise<unknown> = Promise.resolve();
  /** Null until the store is open or refused. */
  private readonly opening = signal<StoreOpening | null>(null);
  private readonly sessionState = signal<SessionState | null>(null);
  private readonly loaded = signal(false);
  private readonly ready = signal(false);
  private readonly bookList = signal<readonly BookSummary[]>([]);
  private readonly bookId = signal<string | null>(null);
  private readonly chapterListing = signal<BookListing | null>(null);
  private readonly openChapter = signal<string | null>(null);
  private readonly editorStatus = signal<AutosaveStatus | null>(null);
  private readonly openEditor = signal<EditorHandle | null>(null);
  private readonly deviceSettings = signal<DeviceSettings>(DEFAULT_SETTINGS);
  private readonly currentNotice = signal<Notice | null>(null);
  private readonly long = signal(false);
  private readonly keptCopies = signal<readonly KeptCopy[]>([]);
  private readonly openFailure = signal<unknown>(undefined);
  private readonly deviceIdSignal = signal<string | null>(null);
  private readonly readings = signal(0);
  private readonly lastReplace = signal<LastReplace | null>(null);
  /** Part 19e, NR7: the open book's notes, tombstones included; null before they are read. */
  private readonly noteListing = signal<NoteListing | null>(null);
  /**
   * Part 19e, table CP: the composers' typing, by composer (each chapter's, each thread's reply, each book's Notebook),
   * with the book it was typed in (table CK: kept in the page's record as the page goes).
   */
  /** CK11: the turn of the composers' last keystroke, so only the last one's pause writes the record. */
  private composerTurn = 0;
  private readonly composers = signal<ReadonlyMap<string, { readonly bookId: string; readonly text: string }>>(new Map());
  /** Part 19e, table AR: where the open chapter's notes were last found. */
  private readonly foundNotes = signal<FoundNotes | null>(null);
  /**
   * Part 39, AC1: by `link chapter` (a reading link's record ID and a chapter it shares), the visible text of the snapshot
   * the link pinned for that chapter: the base a reader's note on it was made in; null when there is none or it can't be
   * read. Snapshots never change, so a text once read is kept.
   */
  private readonly noteBases = new Map<string, string | null>();
  /** Part 39, AC1: whether the bases are being read. */
  private basesLoading = false;
  /** Part 19c, table N: typing in notes not stored yet, by {@link targetKey}. */
  private readonly drafts = new Map<string, NoteEdit>();
  /** Part 19c, N2: the latest keystroke's turn; an earlier pause's timer finds it changed and stores nothing. */
  private noteTurn = 0;
  /** Part 19c, N9: the notes' writes, one at a time, whoever asks (a pause, a close, the session letting go). */
  private notesWriting: Promise<void> = Promise.resolve();
  /** Fix-next 1, principle 3: this page's record key, made when it first writes one. */
  private unsavedKey: string | null = null;
  /** Fix-next 1, U9: the record this page wrote last and hasn't removed, or null. */
  private keptUnsaved: string | null = null;
  /** Fix-next 1 (*deep-fix r2*): the value each notes' write running is storing, by {@link targetKey}. */
  private readonly notesSaving = new Map<string, string>();
  /** Fix-next 1 (*deep-fix r2*): whether the last notes' write failed (N7), so the page asks before it goes. */
  private notesFailing = false;

  constructor(options: WorkspaceOptions) {
    this.options = options;
  }

  /** The screen to show (table S). */
  readonly screen = (): Screen => screenFor({ opening: this.opening.get(), session: this.sessionState.get(), loaded: this.loaded.get() });

  /** Whether the writer may type and change the book now (S1–S9). */
  readonly editable = (): boolean =>
    canEdit({ opening: this.opening.get(), session: this.sessionState.get(), loaded: this.loaded.get() }) && this.ready.get();

  /**
   * Part 36a, table RO: whether the writer may change the books now: the tab may edit (S1–S9) and the account allows it.
   * Opening books and chapters, find, export and settings ask only {@link editable}.
   */
  readonly writable = (): boolean => this.editable() && this.permitted();

  /** Every book on this device, oldest first (B1). */
  readonly books = (): readonly BookSummary[] => this.bookList.get();

  /** The open book's ID, or null (part 19a: what a search's results belong to). */
  readonly openBookId = (): string | null => this.bookId.get();

  /** The open book, or null. */
  readonly book = (): BookSummary | null => this.bookList.get().find((book) => book.id === this.bookId.get()) ?? null;

  /** The open book's chapters, trash, orphan copies and unreadable records (part 08: never hidden). */
  readonly listing = (): BookListing | null => this.chapterListing.get();

  /** Typing kept as conflict copies of the open book's chapters, with the chapter each is of (null once deleted forever). */
  readonly copies = (): readonly KeptCopy[] => this.keptCopies.get();

  /** The open chapter's ID, or null. */
  readonly chapterId = (): string | null => this.openChapter.get();

  /** Where the open chapter's typing stands (decision 3: the unsaved indicator), or null with no chapter open. */
  readonly status = (): AutosaveStatus | null => this.editorStatus.get();

  /** The open chapter's editor, from the moment it opens until it has closed; null with none (part 15: the preview reads it). */
  readonly editor = (): EditorHandle | null => this.openEditor.get();

  /** This device's settings. */
  readonly settings = (): DeviceSettings => this.deviceSettings.get();

  /** What the writer is being told, or null. */
  readonly notice = (): Notice | null => this.currentNotice.get();

  /** Whether the open chapter is long enough to warn about and offer a split (decision 14). */
  readonly longChapter = (): boolean => this.long.get();

  /**
   * Part 15a, deep-fix r4: how many times the session has read the open book since the page started (each time it holds
   * the lock: start, "use here", a restore from the back-forward cache). Another tab may have changed any chapter in
   * between, so the preview reads the chapters it shelves again when this changes.
   */
  readonly bookReading = (): number => this.readings.get();

  /** Part 19a, F7, U7: how many chapters the last replace-all changed in the open book, while it can be undone; else null. */
  readonly replaceUndo = (): number | null => {
    const last = this.lastReplace.get();
    return last?.bookId !== this.bookId.get() ? null : last.chapters.length;
  };

  /** This device's ID once known (the trash says which deletions were made here), else null. */
  readonly deviceId = (): string | null => this.deviceIdSignal.get();

  /** Why the store couldn't open or the session failed, for the `failed` screen. */
  readonly failure = (): unknown => this.openFailure.get();

  /** Part 19e, NR7: the open book's notes, tombstones included, and the records refused; null before they are read. */
  readonly notes = (): NoteListing | null => this.noteListing.get();

  /** Part 19e, table AR: where the open chapter's notes were last found (each standing anchored first note), or null. */
  readonly found = (): FoundNotes | null => this.foundNotes.get();

  /** Part 19e, CP1: what the composer `key` holds (typed, not saved yet). */
  composerText(key: string): string {
    return this.composers.get().get(key)?.text ?? '';
  }

  /** Part 19e, CK10: the open book's Notebook composer's key (none to type in with no book open). */
  notebookComposer(): string {
    return `notebook:${this.openBookId() ?? ''}`;
  }

  /** Part 19e, CP1: the writer typed `value` in the composer `key`; kept in the workspace, whatever closes or changes. */
  typeComposer(key: string, value: string): void {
    const bookId = this.openBookId();
    // Part 36a, RO: a composer is read-only while the account doesn't allow changes, so no words reach a note then.
    if (bookId === null || !this.permitted()) return;
    const next = new Map(this.composers.get());
    if (value === '') next.delete(key);
    else next.set(key, { bookId, text: value });
    this.composers.set(next);
    // CK4: a composer emptied leaves a record kept as the page was hidden (its words now a note, or dropped by the writer).
    if (value === '' && this.keptUnsaved !== null) this.keepUnsaved();
    // CK11 (deep-fix r6): words typed are in the page's record a pause after the last key, the page shown or not, as a
    // note's field is stored (a crash or a killed page in front loses no more than the pause).
    const turn = ++this.composerTurn;
    void this.options.delay(NOTE_DELAY_MS).then(() => {
      if (turn === this.composerTurn) this.keepUnsaved();
    });
  }

  /**
   * Part 19e, NO1: the passage chosen in the open chapter now, as a note's anchor (its range, quote and context in the
   * chapter's visible text); null with none chosen, or one of markup alone.
   */
  chosenPassage(): NoteAnchor | null {
    const editor = this.slot?.editor;
    if (editor === undefined) return null;
    const places = new ChapterPlaces(editor.parsed);
    const range = places.visibleOf(editor.selection.from, editor.selection.to);
    return range === null ? null : places.placeAt(range);
  }

  /**
   * Part 19e, NO1–NO5, NO13: stores a new note of the writer's in the open book; its ID, or null when none was made (the
   * writer told: NO11, NO12).
   */
  createNote(draft: NoteDraft): Promise<string | null> {
    const made: { id: string | null } = { id: null };
    return this.change(async (device) => {
      const bookId = this.bookId.get();
      if (bookId === null) return;
      const created = await device.notes.create({ ...draft, bookId });
      if (created.kind === 'created') made.id = created.note.id;
      else this.noteFailed(created);
      if (created.kind === 'created') this.notesChanged([created.note]);
      await this.refresh(device);
    }).then(() => made.id);
  }

  /** Part 19e, NO6: resolves or reopens a thread, its anchor written as found now (C7: the writer acted on it). */
  resolveNote(noteId: string, resolved: boolean): Promise<void> {
    return this.change(async (device) => {
      this.noteFailed(await device.notes.resolve(noteId, resolved, this.anchorNow(noteId)));
      await this.refresh(device);
    });
  }

  /** Part 19e, NO8: deletes a note (the interface asks first), a first note's replies with it, their typing not stored. */
  deleteNote(noteId: string): Promise<void> {
    return this.change(async (device) => {
      const gone = [noteId, ...(this.noteListing.get()?.notes ?? []).filter((note) => note.parentId === noteId).map((note) => note.id)];
      for (const id of gone) for (const field of ['text', 'title', 'group'] as const) this.drafts.delete(targetKey({ kind: 'note', id, field }));
      const removed = await device.notes.remove(noteId);
      this.noteFailed(removed);
      if (removed.kind === 'done') this.notesChanged((this.noteListing.get()?.notes ?? []).filter((note) => gone.includes(note.id)));
      await this.refresh(device);
    });
  }

  /** Part 19e, D′11: selects note `noteId`'s passage in the open chapter, as found now, and gives the text the focus. */
  goToNote(noteId: string): void {
    const now = this.locateNow(noteId);
    const range = now?.range ?? null;
    if (now === undefined || range === null) return;
    this.slot?.editor.goTo(now.places.sourceOf(range));
  }

  /**
   * Part 19c, N1: a note's text as the writer last typed it: the typing not stored yet, else the stored text in the open
   * book's listing; empty for a chapter or note it doesn't hold.
   */
  noteText(target: NoteTarget): string {
    // The listing is read first, always, so a view following this follows every stored change (deep-fix r1).
    const stored = this.storedNote(target);
    return this.drafts.get(targetKey(target))?.value ?? stored ?? '';
  }

  /** Part 19c, N13: whether a note's field holds typing not stored yet. */
  typingNote(target: NoteTarget): boolean {
    return this.drafts.has(targetKey(target));
  }

  /**
   * Part 19c, N1, N2, N13: the writer typed `value` in a note's field: kept at once (never lost to a closing dialog or
   * screen), and stored {@link NOTE_DELAY_MS} after the last keystroke. The typing's base is what the field showed when it
   * started (`shown`; the stored text when not given), never the stored text then, which another tab or a join may have
   * changed under the field (deep-fix r1).
   */
  typeNote(target: NoteTarget, value: string, shown?: string): void {
    // Part 36a, RO: a note's field is read-only while the account doesn't allow changes; typing kept before still stores.
    if (!this.permitted()) return;
    const key = targetKey(target);
    const base = this.drafts.get(key)?.base ?? shown ?? this.storedNote(target) ?? '';
    this.drafts.set(key, { target, base, value });
    const turn = ++this.noteTurn;
    void this.options.delay(NOTE_DELAY_MS).then(() => {
      if (turn === this.noteTurn) void this.flushNotes();
    });
  }

  /** Part 19c, N3–N5: stores the notes' typing now (the dialog closing, the page hidden), after the changes queued before. */
  flushNotes(): Promise<void> {
    return this.withDevice((device) => this.writeNotes(device));
  }

  /**
   * Part 16b, I6: adds an image the image worker made to book `bookId` through the store open now (part 16a, table A: an
   * add needs no lock, so it runs at once, outside the queue of the writer's changes); `failed` with no store open.
   */
  addImage(bookId: string, image: EncodedImage): Promise<ImageAddOutcome> {
    const connection = this.connection;
    if (connection === null) return Promise.resolve({ kind: 'failed', error: new Error('the store is not open') });
    // Part 36a, RO: no image is added to a book the writer may not change.
    if (!this.permitted()) return Promise.resolve({ kind: 'failed', error: new Error('the books are read-only') });
    return new ImageLibrary({ store: connection.store, digest: this.options.digest, newId: this.options.newId }).add(bookId, image);
  }

  /**
   * Part 16b, I12: where images go now: the open book and its open chapter's editor, while the writer may edit; null
   * with no book or chapter open, or the workspace paused.
   */
  imagePlace(): { readonly bookId: string; readonly editor: EditorHandle } | null {
    const book = this.book();
    const editor = this.editor();
    return book === null || editor === null || !this.writable() ? null : { bookId: book.id, editor };
  }

  /** L0: opens the store and starts the session; the book loads once the session holds the lock. */
  start(): Promise<void> {
    this.options.persist();
    return this.connect();
  }

  /** "Use here" (L1): takes the lock from the other tab; the book loads again once it holds it. */
  useHere(): Promise<void> {
    const connection = this.connection;
    return connection === null ? Promise.resolve() : connection.session.useHere().then(() => undefined);
  }

  /**
   * L2: the page is visible again: a tab back from the background checks it still holds the lock. Fix-next 1, U6: the
   * page lives on and saves as usual, so its record goes.
   */
  visible(): void {
    // CK11: a record holding a composer's words stays (written again as it is now); without, it goes as before.
    if (this.composers.get().size === 0) this.dropUnsaved();
    else this.keepUnsaved();
    void this.connection?.session.check();
  }

  /**
   * L3: the page is hidden: the open chapter's pending typing starts saving now (decision 39), and what isn't stored yet
   * is kept in this page's record in the same task, since the page may never run another (fix-next 1, U1, U2).
   */
  hidden(): void {
    void this.slot?.editor.flush();
    // Part 19c, N4.
    if (this.drafts.size > 0) void this.flushNotes();
    this.keepUnsaved();
  }

  /** L4: the page is going away: the session saves the typing and lets go (part 06, E17); fix-next 1, U5: kept as well. */
  pageHide(): void {
    void this.connection?.session.close();
    this.keepUnsaved();
  }

  /**
   * Part 36a, RO: the account's permit changed: when writing stops, the open editor stops taking input and saves what it
   * holds; when it starts again, the editor takes input once more if the tab may edit.
   */
  permitChanged(): void {
    const editor = this.slot?.editor;
    if (editor === undefined) return;
    editor.pause(!this.writable());
    if (!this.permitted()) void editor.flush();
  }

  /** Part 36a: whether the account lets the books change (the tab's lock aside). */
  private permitted(): boolean {
    return this.options.permit?.() ?? true;
  }

  /** L5: the page is shown; one restored from the back-forward cache opens a new session (part 06, E22). */
  pageShow(persisted: boolean): void {
    if (persisted && this.sessionState.get() === 'closed') {
      // Fix-next 1, U6: the page restored holds its typing again.
      this.dropUnsaved();
      void this.connect();
    }
    else this.visible();
  }

  /** Clears the notice. */
  dismissNotice(): void {
    this.currentNotice.set(null);
  }

  /**
   * C1: opens a chapter of the open book, after saving the one open now; only one live in the book's listing, so a stale
   * caller (find's results, part 19a deep-fix r1) never opens a chapter in the trash or deleted forever.
   */
  openChapterById(chapterId: string): Promise<void> {
    return this.edit(async (device) => {
      if (this.slot?.chapterId === chapterId || !this.chapters().some((entry) => entry.id === chapterId)) return;
      if (!(await this.closeEditor())) return;
      await this.load(device, chapterId);
    });
  }

  /** C2: opens the chapter `step` places after (or before, for −1) the open one, if there is one. */
  stepChapter(step: 1 | -1): Promise<void> {
    return this.edit(async (device) => {
      const chapters = this.chapters();
      const index = chapters.findIndex((entry) => entry.id === this.openChapter.get());
      const next = index < 0 ? undefined : chapters[index + step];
      if (next === undefined || !(await this.closeEditor())) return;
      await this.load(device, next.id);
    });
  }

  /** C3: a new chapter right after the open one (at the end with none open), opened. */
  createChapter(): Promise<void> {
    return this.change(async (device) => {
      const bookId = this.bookId.get();
      if (bookId === null) return;
      const chapters = this.chapters();
      const index = chapters.findIndex((entry) => entry.id === this.openChapter.get());
      const before = index < 0 ? null : (chapters[index + 1]?.id ?? null);
      const created = await device.book.create(bookId, before);
      if (created.kind !== 'created') { this.report(created); return; }
      await this.refresh(device);
      if (await this.closeEditor()) await this.load(device, created.entry.id);
    });
  }

  /** B4: renames a chapter, its title cleaned ({@link cleanTitle}); an empty title shows as untitled. */
  renameChapter(chapterId: string, title: string): Promise<void> {
    return this.change(async (device) => {
      this.report(await device.book.update(chapterId, { title: cleanTitle(title) }));
      await this.refresh(device);
    });
  }

  /** B5: moves a chapter before another, or to the end for null (decision 62). */
  moveChapter(chapterId: string, before: string | null): Promise<void> {
    return this.change(async (device) => {
      this.report(await device.book.move(chapterId, before));
      await this.refresh(device);
    });
  }

  /**
   * C4: moves a chapter to the trash. The open one is saved and closed first (part 08, X2), and the chapter after it
   * opens, or the one before.
   */
  trashChapter(chapterId: string): Promise<void> {
    return this.change(async (device) => {
      // The open chapter, editor or not (one whose text couldn't be read has none, C7).
      const open = this.openChapter.get() === chapterId;
      const chapters = this.chapters();
      const index = chapters.findIndex((entry) => entry.id === chapterId);
      const neighbour = chapters[index + 1] ?? chapters[index - 1];
      if (open && !(await this.closeEditor())) return;
      const result = await device.book.trash(chapterId);
      this.report(result);
      await this.refresh(device);
      if (open) await this.load(device, result.kind === 'done' ? (neighbour?.id ?? null) : chapterId);
    });
  }

  /** B6: brings a chapter back from the trash. */
  restoreChapter(chapterId: string): Promise<void> {
    return this.change(async (device) => {
      this.report(await device.book.restore(chapterId));
      await this.refresh(device);
    });
  }

  /** B7: deletes a chapter in the trash forever (the interface asks first). */
  deleteForever(chapterId: string): Promise<void> {
    return this.change(async (device) => {
      this.report(await device.book.deleteForever(chapterId));
      await this.refresh(device);
    });
  }

  /** B8: empties the open book's trash (the interface asks first). */
  emptyTrash(): Promise<void> {
    return this.change(async (device) => {
      const bookId = this.bookId.get();
      if (bookId === null) return;
      this.report(await device.book.emptyTrash(bookId));
      await this.refresh(device);
    });
  }

  /** B9: a new chapter, at the end, holding a conflict copy's text whose chapter is gone (part 08, X4); opened. */
  recoverCopy(copyId: string): Promise<void> {
    return this.change(async (device) => {
      const copy = this.keptCopies.get().find((each) => each.copy.id === copyId)?.copy;
      if (copy === undefined) return;
      const created = await device.book.create(copy.bookId, null, { text: copy.text });
      if (created.kind !== 'created') { this.report(created); return; }
      // The copy goes once its text is a chapter, so it can't be made one twice; written after, so a failure leaves
      // both rather than neither (*deep-fix r4*).
      this.report(await device.connection.session.write([{ kind: 'delete', store: 'conflictCopies', id: copy.id }]));
      await this.refresh(device);
      if (await this.closeEditor()) await this.load(device, created.entry.id);
    });
  }

  /** B9: deletes a kept copy the writer no longer wants (the interface asks first) (*deep-fix r4*). */
  discardCopy(copyId: string): Promise<void> {
    return this.change(async (device) => {
      this.report(await device.connection.session.write([{ kind: 'delete', store: 'conflictCopies', id: copyId }]));
      await this.refresh(device);
    });
  }

  /**
   * L4: the page may be about to go (`beforeunload`): typing not yet stored starts saving now and is kept in this page's
   * record (fix-next 1, U3); true says the browser should ask first, when there is such typing and the record couldn't be
   * written (U4), since a write started as the page goes may never commit (*deep-fix r4*). Asking gives the save no time
   * (the page runs nothing while the browser asks), only the writer the choice to stay while it lands.
   */
  leaving(): boolean {
    // U4b (*deep-fix r2*): while the store refuses saves, the next page's merge likely fails too, so the writer is asked
    // as before (*deep-fix r3*), with the record written all the same. Read before the flush below starts a save.
    const refused = this.slot?.editor.status === 'failed' || (this.notesFailing && this.drafts.size > 0);
    // Part 19c, N6: notes' typing not stored yet counts as unsaved typing.
    if (this.drafts.size > 0) void this.flushNotes();
    if (this.typingUnsaved()) void this.slot?.editor.flush();
    // Part 19e, CP2, CK3: a composer's words are kept in the record too; the browser asks only when it can't be written.
    return !this.keepUnsaved() || refused;
  }

  /**
   * Part 40a, table N: stores the open chapter's typing before the page reloads into a new build, after everything queued
   * before it. True when stored: none open (N1), or saved as the chapter or a copy (N2), the editor then left paused so
   * nothing is typed before the page goes; false when the save failed (N3), the editor paused as the session's state says.
   * Never rejects.
   */
  settle(): Promise<boolean> {
    const result = { stored: false };
    return this.serial(async () => {
      result.stored = await settleEditor(this.slot?.editor ?? null, this.editable);
      // Part 19c, N8: the notes' typing too.
      const device = this.device;
      if (device !== null && this.drafts.size > 0) await this.writeNotes(device);
      result.stored &&= this.drafts.size === 0;
    }, () => undefined).then(() => result.stored);
  }

  /**
   * Part 36a, BK8, BK12: the page goes to another account's store: the typing stored (table N), then `go` (the reload);
   * typing the store refused and the page's record couldn't keep either (or the store refuses saves, U4b) keeps the page,
   * read-only, so the browser asks before it goes. Never rejects.
   */
  async leaveWith(go: () => void): Promise<void> {
    if ((await this.settle()) || !this.leaving()) go();
  }

  /** C5: the open chapter's text now and its split points, for the writer to pick from (part 08, L7); null with none open. */
  splitOffer(): SplitOffer | null {
    const slot = this.slot;
    if (slot === null) return null;
    const { parsed } = slot.editor;
    return { text: parsed.text, points: splitPoints(parsed) };
  }

  /**
   * C5: splits the open chapter at `at`, points of `offer` (part 08, L7). The editor is saved and closed first so
   * nothing types into the text being split, then the first piece opens. A stored text that isn't the offer's (the
   * writer typed after it was made) isn't split, so a point never lands somewhere else; a refused split opens the
   * chapter as it was.
   */
  split(offer: SplitOffer, at: readonly number[]): Promise<void> {
    return this.change(async (device) => {
      const slot = this.slot;
      if (slot === null || !(await this.closeEditor())) return;
      const stored = await readText(device.connection, slot.chapterId);
      if (stored !== offer.text) this.currentNotice.set({ kind: 'split-refused', problem: 'not-a-point' });
      else {
        const result = await device.book.split({ id: slot.chapterId, bookId: slot.bookId, text: stored }, at);
        if (result.kind === 'refused') this.currentNotice.set({ kind: 'split-refused', problem: result.error.problem });
        else this.report(result);
        await this.refresh(device);
      }
      await this.load(device, slot.chapterId);
    });
  }

  /**
   * B10: a new book with one empty chapter, opened (decision 13); part 11h: its details as the New book window gives them
   * (`details` shapes the new book's, title cleaned as a rename's). Its ID, or null when nothing was stored.
   */
  createBook(details: (metadata: BookMetadata) => BookMetadata = (metadata) => metadata): Promise<string | null> {
    const result: { id: string | null } = { id: null };
    return this.change(async (device) => {
      const book = await this.newBook(device, details);
      result.id = book;
      if (book !== null && (await this.closeEditor())) await this.openBookNow(device, book);
    }).then(() => result.id);
  }

  /**
   * Part 11h, BW8: Book details' Save changes: the open book's details changed by `change` in one write (each field the
   * window shows); a book whose record can't be read keeps it untouched (B3), and details the record can't hold are
   * refused whole, said as a refused title is.
   */
  changeDetails(change: (metadata: BookMetadata) => BookMetadata): Promise<void> {
    return this.changeBook((metadata) => {
      const changed = change(metadata);
      return { ...changed, title: cleanTitle(changed.title) || metadata.title };
    });
  }

  /**
   * Part 11h, BW11: the image `reference` names in book `bookId`, its bytes checked against the reference's hash as the
   * exports check theirs, to draw a cover photo; null with no store open, or an image missing or not the one named.
   */
  async coverImage(bookId: string, reference: ImageReference): Promise<{ readonly bytes: Uint8Array; readonly format: string } | null> {
    const connection = this.connection;
    if (connection === null) return null;
    try {
      const { store } = connection;
      const [image, bytes] = await Promise.all([store.readImage(bookId, reference.id), store.readImageBytes(bookId, reference.id)]);
      return image?.sha256 === reference.sha256 && bytes !== undefined ? { bytes: bytes.bytes, format: image.format } : null;
    } catch {
      return null;
    }
  }

  /**
   * Part 18, V8, V10, V11 (principle 5): stores an imported book as a new book: its record (title and subtitle cleaned
   * as part 05 does titles), every chapter and its text in one write, then opens it at its first chapter. The open
   * chapter is saved and closed first; when its save fails, or the workspace can't be edited, nothing is stored.
   */
  importBook(request: CommitRequest): Promise<CommitResult> {
    const result: { value: CommitResult } = { value: { kind: 'not-editable' } };
    return this.change(async (device) => {
      if (!(await this.closeEditor())) {
        result.value = { kind: 'save-failed' };
        return;
      }
      const metadata = parseMetadata({ ...newMetadata(cleanTitle(request.title) || cleanTitle(this.options.untitledBook())), subtitle: cleanTitle(request.subtitle ?? '') });
      const record: BookRecord = { id: request.bookId, metadata: serializeMetadata(metadata), theme: serializeTheme(DEFAULT_THEME), createdAt: this.options.now() };
      const created = await device.book.createBook(record, request.chapters.map((chapter) => ({ title: cleanTitle(chapter.title ?? ''), text: chapter.text })));
      if (created.kind !== 'created') {
        result.value = notCreated(created);
        // The open chapter goes back to the editor: nothing changed in its book.
        await this.load(device, this.openChapter.get());
        return;
      }
      result.value = { kind: 'created', chapters: created.chapters.length };
      await this.loadBooks(device.connection);
      await this.openBookNow(device, request.bookId);
    }).then(() => result.value);
  }

  /** Part 24c, table HB: whether anything of book `bookId` is on this device, readable or not; null when that can't be read. */
  bookHere(bookId: string): Promise<boolean | null> {
    const result: { value: boolean | null } = { value: null };
    return this.withDevice(async (device) => {
      result.value = await device.connection.store.hasBook(bookId);
    }).then(() => result.value);
  }

  /**
   * Part 24c, S1, L3, principle 6: a bundle import of book `bookId` begun in this turn of the lock: its marker written in
   * the history's queue once nothing of the book is here. The open chapter stays open and editable (deep-fix r2): the
   * import writes only the hidden book until it is whole.
   */
  beginImport(bookId: string): Promise<ImportStart> {
    const result: { value: ImportStart } = { value: { kind: 'not-editable' } };
    return this.change(async (device) => {
      const { store, session } = device.connection;
      result.value = await BundleImport.begin({ store, write: session.writer(), history: device.history, now: this.options.now, newId: this.options.newId }, bookId);
    }).then(() => result.value);
  }

  /**
   * Part 24c, S3a (deep-fix r2): the book a bundle import just made whole, listed; nothing else changes, since the writer
   * may be typing (3.2.5). Opening it is the writer's to ask (`openBook`, S3c, S3d).
   */
  listImported(): Promise<void> {
    return this.withDevice((device) => this.loadBooks(device.connection));
  }

  /**
   * Part 24d: `read` run over this device's store after everything queued before it: one short read of an update's
   * review, so the planning between reads holds nothing up; null when no store is open or the read failed.
   */
  readForUpdate<T>(read: (store: Store) => Promise<T>): Promise<T | null> {
    const result: { value: T | null } = { value: null };
    return this.withDevice(
      async (device) => {
        result.value = await read(device.connection.store);
      },
      // The update says a failed read itself (its `read-failed`, or a chapter not decided).
      () => undefined,
    ).then(() => result.value);
  }

  /**
   * Part 24d, S′5, S′6: a staged update written into its book (`BundleImport.update`). When the book is the open one, the
   * notes' typing is stored and the open chapter saved and closed first (23b's AP6; the writer asked, by Apply), then
   * opened again, whatever happened; the book list is read again (the book's title may change). Nothing is written while
   * the workspace can't be edited, or when the save fails. Once written, `handover` (each chapter whose text is now the
   * bundle's) is pinned in this device's history (H′3).
   */
  applyUpdate(running: BundleImport, request: UpdateRequest, handover: readonly ChapterText[]): Promise<UpdateCommitResult> {
    const result: { value: UpdateCommitResult } = { value: { kind: 'not-editable' } };
    return this.change(async (device) => {
      const open = request.bookId === this.bookId.get();
      if (open) {
        await this.writeNotes(device);
        if (!(await this.closeEditor())) {
          result.value = { kind: 'save-failed' };
          return;
        }
      }
      const chapter = this.openChapter.get();
      try {
        result.value = await running.update(request);
        if (result.value.kind === 'updated') this.textsChanged(request.bookId);
        // H′3 (deep-fix r3): each chapter whose text is now the bundle's, pinned as handed over; a pin not written leaves
        // the update written.
        if (result.value.kind === 'updated' && handover.length > 0) await device.history.pinExport(handover, exportPinUntil(this.options.now()), 'bundle');
      } finally {
        await this.loadBooks(device.connection);
        if (open) {
          await this.refresh(device);
          await this.load(device, chapter);
        }
      }
    }).then(() => result.value);
  }

  /** B11: opens another book at its last open chapter. */
  openBook(bookId: string): Promise<void> {
    return this.edit(async (device) => {
      if (bookId === this.bookId.get() || !(await this.closeEditor())) return;
      await this.openBookNow(device, bookId);
    });
  }

  /** B12: renames the open book; a book whose record can't be read keeps it untouched (B3). */
  renameBook(title: string): Promise<void> {
    return this.changeBook((metadata) => ({ ...metadata, title: cleanTitle(title) }));
  }

  /** B13: sets the open book's language (decision 33); the open chapter reopens with its spellchecker in it. */
  setLanguage(language: BookLanguage): Promise<void> {
    return this.changeBook((metadata) => ({ ...metadata, language }));
  }

  /** G8: changes this device's settings, applies them, and stores them. */
  updateSettings(change: Partial<DeviceSettings>): Promise<void> {
    const next = parseSettings(serializeSettings({ ...this.deviceSettings.get(), ...change }), this.options.platform, this.options.reserved);
    this.deviceSettings.set(next);
    this.slot?.editor.setAutoConversion(next.autoConversion);
    this.restyle();
    // Part 19e, MK2: the writer's marks shown or hidden at once.
    this.findNotes();
    return this.edit(async (device) => {
      await this.storeSettings(device);
    });
  }

  /** The keys in effect. */
  readonly keys = (): KeyBindings => this.deviceSettings.get().keys;

  /** Part 19b, G1: a book's goals and today's words on this device. */
  readonly writing = (bookId: string): BookWriting => this.deviceSettings.get().writing[bookId] ?? NO_WRITING;

  /**
   * Part 19b, G1, D2, D8: changes a book's goals or today's words and stores them while this tab may write; otherwise
   * they are kept in memory only (settings are read again when the tab takes the lock back). Nothing else is applied
   * again, as `updateSettings` does: a count lands after every pause.
   */
  changeWriting(bookId: string, change: Partial<BookWriting>): Promise<void> {
    this.deviceSettings.set(changeWriting(this.deviceSettings.get(), bookId, change));
    return this.edit(async (device) => {
      await this.storeSettings(device);
    });
  }

  /** Part 10a, S4: an editor's paragraph look in `bookId`: this device's choice, the book look from that book's theme. */
  private paragraphLook(bookId: string): ParagraphLook {
    const theme = this.bookList.get().find((book) => book.id === bookId)?.theme ?? DEFAULT_THEME;
    return paragraphLookOf(this.deviceSettings.get().paragraphLook, theme);
  }

  /** S3, S5: the open editor in the look its book and this device's choice give now. */
  private restyle(): void {
    const slot = this.slot;
    slot?.editor.setParagraphLook(this.paragraphLook(slot.bookId));
  }

  /**
   * Runs a change of the writer's (W1): one at a time, and only while this tab holds the lock and the book is read;
   * otherwise nothing happens (the interface offers nothing then).
   */
  private edit(body: (device: Device) => Promise<void>): Promise<void> {
    return this.withDevice(async (device) => {
      if (this.editable()) await body(device);
    });
  }

  /**
   * Part 36a, table RO: runs a change of the books (W1) as {@link edit} does, and only while the account allows it
   * ({@link writable}); otherwise nothing happens (the interface offers nothing then).
   */
  private change(body: (device: Device) => Promise<void>): Promise<void> {
    return this.withDevice(async (device) => {
      if (this.writable()) await body(device);
    });
  }

  /**
   * Runs `body` in the queue with this device, once one is known; a read that throws (the store failing under it) is
   * said (W5: writes report their own failures), unless `failed` says it otherwise.
   */
  private withDevice(
    body: (device: Device) => Promise<void>,
    failed: () => void = () => {
      this.currentNotice.set({ kind: 'read-failed' });
    },
  ): Promise<void> {
    return this.serial(async () => {
      const device = this.device;
      if (device !== null) await body(device);
    }, failed);
  }

  /**
   * Runs `body` after everything queued before it; an error it throws goes to `failed`, so the queue always goes on
   * and nothing the workspace returns ever rejects.
   */
  private serial(body: () => Promise<void>, failed: (error: unknown) => void): Promise<void> {
    const run = this.queue.then(body).catch(failed);
    this.queue = run;
    return run;
  }

  /** L0, L8, S10, S11: the store couldn't be opened or read: the outdated or failed screen, with the reason. */
  private storeFailed(error: unknown): void {
    batch(() => {
      this.opening.set(error instanceof StoreOutdatedError ? 'outdated' : 'failed');
      this.openFailure.set(error);
    });
  }

  /**
   * L0, L5, L6: opens a store and a session on it, replacing the last ones; the editor stays open, paused, and saves
   * through the new ones (part 06, V6). The replaced session is closed, which lets go of its tab lock and channel; it
   * saves nothing then, since its typing is the new session's to save.
   */
  private connect(): Promise<void> {
    return this.serial(async () => {
      // The previous session's last state (`closed`, `disconnected`) already paused the editor and made it not ready.
      const store = await this.options.openStore();
      const previous = this.connection;
      const session: EditSession = new EditSession({
        store,
        locks: this.options.locks,
        channel: this.options.channel(),
        ...(this.options.space === undefined ? {} : { editLock: this.options.space().editLock }),
        delay: this.options.delay,
        newId: this.options.newId,
        now: this.options.now,
        saveUnsaved: async () => {
          if (this.connection !== connection) return;
          await this.slot?.editor.flush();
          // Part 19c, N5: before another tab may have the lock, the notes' typing too, through this connection only.
          const device = this.device;
          if (device?.connection === connection && this.drafts.size > 0) await this.writeNotes(device);
        },
        onState: (state) => {
          if (this.connection === connection) this.onState(state, connection);
        },
      });
      const connection: Connection = { store, session };
      this.connection = connection;
      if (this.slot !== null) this.slot.connection = connection;
      this.opening.set('open');
      this.sessionState.set(session.state);
      void previous?.session.close();
      await session.start();
    }, (error) => {
      this.storeFailed(error);
    });
  }

  /** Table L: what a session's new state means for the editor and the book. */
  private onState(state: SessionState, connection: Connection): void {
    batch(() => {
      this.sessionState.set(state);
      this.ready.set(false);
      // S5: why the session failed, and nothing once it no longer has.
      this.openFailure.set(connection.session.failure);
    });
    this.slot?.editor.pause(true);
    // S11: a browser without Web Locks can't run the app safely at all; that is the failed screen's to say, not "open
    // in another tab" (*deep-fix r4*).
    if (state === 'failed' && connection.session.failure instanceof LocksUnsupportedError) this.storeFailed(connection.session.failure);
    // L8: a store that throws while the book is read can't be used: the failed screen says so, with a retry.
    if (state === 'active') {
      void this.serial(
        () => this.activated(connection),
        (error) => {
          this.storeFailed(error);
        },
      );
    }
    // L6: a store the browser closed is replaced at once; the editor's typing saves through the new one (part 06, V6).
    if (state === 'disconnected') void this.connect();
  }

  /**
   * L1, L5, L6: the session holds the lock (again): reads the device, the books and the open book, which another tab
   * may have changed, then reconciles the open editor with its stored chapter (W4) or opens the last chapter. A session
   * that lost the lock meanwhile writes nothing and shows nothing here; its next turn reads everything again.
   */
  private async activated(connection: Connection): Promise<void> {
    const device = await this.loadDevice(connection);
    if (device.kind !== 'device') {
      // G1: without this device's record nothing can be written for it: the store is as good as closed. A lost lock
      // (`stale`) is the session's to show.
      if (device.kind === 'failed') this.storeFailed(device.error);
      return;
    }
    this.device = device;
    this.deviceIdSignal.set(device.id);
    // Part 24c, S8: every pending import (crashed, closed, or of an earlier turn) abandoned before the books are listed; one
    // not abandoned stays hidden and is tried again next turn (S9).
    await abandonPending(connection.store, connection.session.writer());
    // Fix-next 1, M9: other pages' typing kept as they went is in the store before the books are listed and a chapter opens.
    await this.recoverUnsaved(device);
    await this.loadBooks(connection);
    let bookId = this.bookId.get() ?? this.deviceSettings.get().lastBook;
    if (bookId === null || !this.bookList.get().some((book) => book.id === bookId)) {
      bookId = this.bookList.get()[0]?.id ?? (this.options.firstBook?.() === false ? null : await this.newBook(device));
    }
    this.bookId.set(bookId);
    await this.refresh(device);
    batch(() => {
      this.loaded.set(true);
      this.ready.set(connection.session.state === 'active');
      this.readings.set(this.readings.get() + 1);
    });
    if (!this.ready.get() || bookId === null) return;
    await this.reconcile(device, bookId);
    // Part 19c, N10: typing in notes this tab couldn't store while another tab had the lock is stored now (table W).
    if (this.drafts.size > 0) await this.writeNotes(device);
    this.options.onReady();
  }

  /**
   * W4: opens the last chapter, or keeps the open editor if its chapter is still the one it holds; when the chapter
   * changed (or went) under it, its typing goes to a conflict copy and the stored chapter opens.
   */
  private async reconcile(device: Device, bookId: string): Promise<void> {
    const slot = this.slot;
    if (slot === null) return this.load(device, this.lastChapter(bookId));
    const live = this.chapters().some((entry) => entry.id === slot.chapterId);
    const stored = await readText(device.connection, slot.chapterId);
    // A chapter live in the open book's listing is in the open book.
    if (live && stored === slot.base) {
      // W6: typing kept only as a conflict copy while the tab was away is still the editor's, and goes to the chapter
      // now that nobody else changed it (*deep-fix r1*).
      if (slot.editor.parsed.text !== stored) slot.editor.resave();
      // Part 36a, RO: typing again only while the account allows it.
      slot.editor.pause(!this.writable());
      return;
    }
    // Table R (*deep-fix r3*): the editor's text goes to a copy when it isn't the stored text and either holds typing
    // its last save didn't store (R4, R6) or the stored text is gone or unreadable, so nothing else holds it (R7). Text
    // the editor only saved, which another tab then changed, is in that tab's edit, as one tab's own edits are (R3).
    slot.diverged = true;
    const text = slot.editor.parsed.text;
    if (text !== stored && (text !== slot.base || typeof stored !== 'string')) slot.editor.resave();
    if (!(await this.closeEditor())) return;
    // A copy just made shows under "Needs your attention" (*deep-fix r3*).
    await this.refresh(device);
    await this.load(device, live ? slot.chapterId : this.lastChapter(bookId));
  }

  /**
   * This device's record (the oldest readable one), made on first use (G1); `stale` when the lock went meanwhile (the
   * session shows that), the failure when it can't be written.
   */
  private async loadDevice(connection: Connection): Promise<Device | { readonly kind: 'stale' } | StoreFailure> {
    // Records come in key order: the first is the one with the lowest ID.
    let record = (await connection.store.readAll('device')).records[0];
    if (record === undefined) {
      record = { id: this.options.newId(), settings: serializeSettings(DEFAULT_SETTINGS) };
      const written = await connection.session.write([{ kind: 'put', store: 'device', value: record }]);
      if (written.kind !== 'committed') return written;
    }
    this.deviceSettings.set(parseSettings(record.settings, this.options.platform, this.options.reserved));
    const write = (ops: readonly WriteOp[]) => connection.session.write(ops);
    const history = new SnapshotHistory({ store: connection.store, write, owner: record.id, now: this.options.now, zone: this.options.zone, newId: this.options.newId });
    const book = new ChapterBook({ store: connection.store, write, history, device: record.id, now: this.options.now, newId: this.options.newId, digest: this.options.digest });
    const notes = new Notes({ store: connection.store, write, history, now: this.options.now, newId: this.options.newId });
    return { kind: 'device', id: record.id, history, book, notes, connection };
  }

  /**
   * B1–B3: every book: each readable record, oldest first, then each book that chapters name but whose record is
   * missing or unreadable, so its chapters are never out of reach.
   */
  private async loadBooks(connection: Connection): Promise<void> {
    const books = await connection.store.readAll('books');
    const entries = await connection.store.readAll('entries');
    // Part 24c, principle 1: a book a pending import is making isn't shown until it is whole.
    const pending = await pendingBooks(connection.store);
    // Records come in key order and the sort is stable, so books made at the same moment go by ID.
    const summaries: BookSummary[] = [...books.records]
      .sort((a, b) => a.createdAt - b.createdAt)
      .map((record) => ({ id: record.id, metadata: metadataOf(record), theme: themeOf(record), stored: true, createdAt: record.createdAt }));
    const unread = books.invalid.map((record) => String(record.key));
    for (const bookId of new Set([...unread, ...entries.records.map((entry) => entry.bookId)])) {
      if (!summaries.some((book) => book.id === bookId)) summaries.push({ id: bookId, metadata: null, theme: DEFAULT_THEME, stored: unread.includes(bookId), createdAt: null });
    }
    this.bookList.set(summaries.filter((book) => !pending.has(book.id)));
    // S5: the open editor follows its book's theme as read again.
    this.restyle();
    // Part 38b, deep-fix r6: the open book's details changed is a change of its copy.
    const open = this.bookId.get();
    if (open !== null) this.copyChanged(open);
  }

  /** B10: stores a new book and its first chapter; returns its ID, or null when the write failed. */
  private async newBook(device: Device, details: (metadata: BookMetadata) => BookMetadata = (metadata) => metadata): Promise<string | null> {
    const id = this.options.newId();
    const untitled = newMetadata(cleanTitle(this.options.untitledBook()));
    let metadata: BookMetadata;
    try {
      // Part 11h, BW5: a blank title is the untitled book's.
      const given = details(untitled);
      metadata = parseMetadata({ ...given, title: cleanTitle(given.title) || untitled.title });
    } catch {
      this.currentNotice.set({ kind: 'title-refused' });
      return null;
    }
    const record: BookRecord = { id, metadata: serializeMetadata(metadata), theme: serializeTheme(DEFAULT_THEME), createdAt: this.options.now() };
    const written = await device.connection.session.write([{ kind: 'put', store: 'books', value: record }]);
    if (written.kind !== 'committed') {
      this.report(written);
      return null;
    }
    this.report(await device.book.create(id, null));
    await this.loadBooks(device.connection);
    return id;
  }

  /** B11: makes `bookId` the open book and opens its last chapter. */
  private async openBookNow(device: Device, bookId: string): Promise<void> {
    this.bookId.set(bookId);
    await this.refresh(device);
    await this.load(device, this.lastChapter(bookId));
  }

  /** Table N (part 21): where the open book's export reads it on `device`: its entries and texts afresh, the open chapter as typed. */
  private exportSource(device: Device): Promise<BookSource | null> {
    return this.bookSource(device, this.bookId.get());
  }

  /**
   * Table N's source of book `bookId` of the list (part 38b, RC11: any book, not only the open one): its entries and texts
   * as stored, the open chapter's as typed when it is this book's. Null for a book not in the list (or none).
   */
  private async bookSource(device: Device, bookId: string | null): Promise<BookSource | null> {
    const summary = this.bookList.get().find((book) => book.id === bookId);
    if (summary === undefined) return null;
    const slot = this.slot?.bookId === summary.id ? this.slot : null;
    return {
      id: summary.id,
      theme: summary.theme,
      metadata: summary.metadata,
      entries: await device.connection.store.readWhere('entries', 'bookId', summary.id),
      text: (chapterId) => readText(device.connection, chapterId),
      open: slot === null ? null : { chapterId: slot.chapterId, text: slot.editor.parsed.text },
      untitled: cleanTitle(this.options.untitledBook()),
      title: cleanTitle,
    };
  }

  /**
   * Table N (part 21): the open book as the PDF export takes it (`bookForExport`), its entries and texts read afresh, the
   * open chapter's text as typed. Null with no book open (N1), or when the book can't be read whole (N7, N9).
   */
  async exportBook(): Promise<PdfBook | null> {
    const result: { book: PdfBook | null } = { book: null };
    await this.withDevice(async (device) => {
      const source = await this.exportSource(device);
      result.book = source === null ? null : await bookForExport(source);
    });
    return result.book;
  }

  /**
   * Part 22, table PN: the open book as the DOCX takes it (table N, `readForExport`) with each chapter's ID, and each
   * chapter's text as exported kept in a snapshot pinned until twelve months after `modified` (`pinExport`, one write),
   * in one queued step, so nothing the writer does comes between the reading and the pinning. `kept` is false when the
   * pins weren't written (PN6: this tab doesn't hold the lock, or the store failed): the export goes on without them.
   * Null with no book open (N1), or when the book can't be read whole (N7, N9).
   */
  async docxBook(modified: number): Promise<DocxBook | null> {
    const result: { book: DocxBook | null } = { book: null };
    await this.withDevice(async (device) => {
      const source = await this.exportSource(device);
      const read = source === null ? null : await readForExport(source);
      if (read === null) return;
      // Fix-next 27, table PT: each pin keeps the chapter's title as exported, so a re-import can merge titles three ways.
      const pinned = await device.history.pinExport(
        read.chapters.map((chapter) => ({ id: chapter.id, bookId: read.book.id, text: chapter.text, title: chapter.title })),
        exportPinUntil(modified),
      );
      const snapshots = pinned.kind === 'pinned' ? pinned.snapshots : new Map<string, string>();
      result.book = { book: read.book, chapters: read.chapters.map((chapter) => ({ id: chapter.id, snapshot: snapshots.get(chapter.id) ?? null })), kept: pinned.kind === 'pinned' };
    });
    return result.book;
  }

  /**
   * Part 38d, LW5: the open book's chapters among `chosen` as a reading link shares them (`readingCopyOf`): read as the
   * exports read them (table N, the open chapter as typed), with the images their text and the cover name that the book
   * holds as named. Null with no book open, or one that can't be read whole (N7, N9).
   */
  async shareCopy(chosen: ReadonlySet<string>): Promise<ShareCopy | null> {
    const result: { copy: ShareCopy | null } = { copy: null };
    await this.withDevice(async (device) => {
      const source = await this.exportSource(device);
      const read = source === null ? null : await readForExport(source);
      if (read === null) return;
      const { store } = device.connection;
      result.copy = await readingCopyOf(read, chosen, async (reference) => {
        const [image, bytes] = await Promise.all([store.readImage(read.book.id, reference.id), store.readImageBytes(read.book.id, reference.id)]);
        return image?.sha256 === reference.sha256 && bytes !== undefined ? { id: image.id, sha256: image.sha256, format: image.format, width: image.width, height: image.height, bytes: bytes.bytes } : null;
      });
    });
    return result.copy;
  }

  /** Parts 38d, 38b: `body`'s answer on this tab's device, in the workspace's queue; `fallback` with no device. */
  private async onDevice<T>(fallback: T, body: (device: Device) => Promise<T>): Promise<T> {
    const result = { value: fallback };
    await this.withDevice(
      async (device) => {
        result.value = await body(device);
      },
      () => undefined,
    );
    return result.value;
  }

  /**
   * Part 38d: the reading links' store over this tab's device (`ShareStore`), each call in the workspace's queue: the
   * records read as stored, a note or a record created with no lock (C6), a record written or deleted and the pins
   * changed under the lock (DP3, DP7). A note stored reads the open book's notes again.
   */
  shareStore(): ShareStore {
    const run = <T>(fallback: T, body: (device: Device) => Promise<T>): Promise<T> => this.onDevice(fallback, body);
    const unpin = (device: Device, record: ShareLinkRecord): Promise<boolean> =>
      record.snapshots.reduce<Promise<boolean>>(async (done, id) => {
        const unpinned = await device.history.unpin(id, (pin) => pin.kind === 'share-link' && pin.linkId === record.id);
        return (await done) && (unpinned.kind === 'committed' || unpinned.kind === 'missing');
      }, Promise.resolve(true));
    return {
      // Deep-fix r7 (part 38d, PL′4): a link of a book a pending import is making isn't pulled, replied through or ended:
      // a comment stored there and acknowledged would go with the book if the import is abandoned. Its items wait at the
      // relay until the book is whole.
      links: () =>
        run<readonly ShareLinkRecord[]>([], async (device) => {
          const pending = await pendingBooks(device.connection.store);
          return (await device.connection.store.readAll('shareLinks')).records.filter((record) => !pending.has(record.bookId));
        }),
      link: (id) =>
        run<ShareLinkRecord | undefined>(undefined, async (device) => {
          const record = await device.connection.store.read('shareLinks', id).catch(() => undefined);
          return record === undefined || (await pendingBooks(device.connection.store)).has(record.bookId) ? undefined : record;
        }),
      note: (id) => run<NoteRecord | undefined>(undefined, (device) => device.connection.store.read('notes', id).catch(() => undefined)),
      readerNotes: (bookId) =>
        run<ReadonlyMap<string, number>>(new Map(), async (device) => {
          const counts = new Map<string, number>();
          for (const note of (await device.connection.store.readWhere('notes', 'bookId', bookId)).records) {
            if (note.origin === 'reader' && note.deletedAt === null && note.link !== undefined) counts.set(note.link.link, (counts.get(note.link.link) ?? 0) + 1);
          }
          return counts;
        }),
      createNote: (note) => run<CreateResult>(NO_STORE, (device) => device.connection.store.create('notes', note)),
      notesStored: () =>
        run(undefined, async (device) => {
          await this.refresh(device);
        }),
      chapterHere: (id) => run(false, async (device) => (await device.connection.store.read('entries', id).catch(() => undefined))?.trashed === null),
      createLink: (record) => run<CreateResult>(NO_STORE, (device) => device.connection.store.create('shareLinks', record)),
      put: (record) => run(false, async (device) => (await device.connection.session.write([{ kind: 'put', store: 'shareLinks', value: record }])).kind === 'committed'),
      remove: (record) =>
        run(false, async (device) => {
          const unpinned = await unpin(device, record);
          return unpinned && (await device.connection.session.write([{ kind: 'delete', store: 'shareLinks', id: record.id }])).kind === 'committed';
        }),
      pin: (bookId, chapters: readonly SharedChapter[], id) =>
        run<readonly string[] | null>(null, async (device) => {
          const pinned = await device.history.pinShare(
            chapters.map((chapter) => ({ id: chapter.id, bookId, text: chapter.text })),
            id,
          );
          // Each chapter's snapshot, in the chapters' order (`pinShare` keeps them so).
          return pinned.kind === 'pinned' ? [...pinned.snapshots.values()] : null;
        }),
      unpin: (record) => run(false, (device) => unpin(device, record)),
    };
  }

  /** Part 38b, DI6: who is told of each save of the open chapter (the book's ID): the invites' copy. */
  private readonly saveListeners = new Set<(bookId: string) => void>();

  /** Part 38b, deep-fix r6 (RC's starts): what the invites' copy last saw of each book's chapters and details. */
  private readonly copyShapes = new Map<string, string>();

  /** Part 38b, deep-fix r8 (RC's starts): book `bookId`'s chapter texts written outside the open chapter's save. */
  private textsChanged(bookId: string): void {
    for (const listener of this.saveListeners) listener(bookId);
  }

  /**
   * Part 38e (DN10): the notes `changed` made, edited or deleted here: when any goes across a share, the save listeners
   * are told of its book, so the reconcile sends it a pause later.
   */
  private notesChanged(changed: readonly NoteRecord[]): void {
    const shared = changed.find((note) => note.shared !== undefined);
    if (shared !== undefined) this.textsChanged(shared.bookId);
  }

  /**
   * Part 38b, deep-fix r6 (RC's starts): tells the save listeners when book `bookId`'s chapters (added, renamed, moved,
   * trashed, restored, deleted) or details (title, metadata, theme) changed since last seen, as a save would; a listing
   * read again with nothing of the copy changed (a note's edit) tells no one.
   */
  private copyChanged(bookId: string): void {
    const listing = this.chapterListing.get();
    const book = this.bookList.get().find((each) => each.id === bookId);
    const shape = JSON.stringify([listing?.chapters.map((entry) => [entry.id, entry.title, entry.position]), listing?.trash.map((entry) => entry.id), book?.metadata, book?.theme]);
    const before = this.copyShapes.get(bookId);
    this.copyShapes.set(bookId, shape);
    if (before !== undefined && before !== shape) for (const listener of this.saveListeners) listener(bookId);
  }

  /** Part 38b, DI6: tells `listener` of each save of the open chapter (a pause in typing); the returned function stops it. */
  onSaved(listener: (bookId: string) => void): () => void {
    this.saveListeners.add(listener);
    return () => {
      this.saveListeners.delete(listener);
    };
  }

  /**
   * Part 38b (table PU, DI3): the invites' store over this tab's device (`InviteStore`), each call in the workspace's
   * queue: the share records as stored (none of a book a pending import is making, as reading links), a record created
   * with no lock (DI3), written or deleted under the lock; and a book as a copy is made of it (table N: as the exports
   * read it, the open chapter as typed; RC11: a closed one as stored), null for a book not in the list.
   */
  inviteStore(): InviteStore {
    const run = <T>(fallback: T, body: (device: Device) => Promise<T>): Promise<T> => this.onDevice(fallback, body);
    return {
      shares: () =>
        run<readonly ShareRecord[]>([], async (device) => {
          const pending = await pendingBooks(device.connection.store);
          return (await device.connection.store.readAll('shares')).records.filter((record) => !pending.has(record.bookId));
        }),
      share: (id) =>
        run<ShareRecord | undefined>(undefined, async (device) => {
          const record = await device.connection.store.read('shares', id).catch(() => undefined);
          return record === undefined || (await pendingBooks(device.connection.store)).has(record.bookId) ? undefined : record;
        }),
      create: (record) => run<CreateResult>(NO_STORE, (device) => device.connection.store.create('shares', record)),
      put: (record) => run(false, async (device) => (await device.connection.session.write([{ kind: 'put', store: 'shares', value: record }])).kind === 'committed'),
      remove: (record) => run(false, async (device) => (await device.connection.session.write([{ kind: 'delete', store: 'shares', id: record.id }])).kind === 'committed'),
      book: (bookId) =>
        run<CopyBook | null>(null, async (device) => {
          const source = await this.bookSource(device, bookId);
          const read = source === null ? null : await readForExport(source);
          if (source === null || read === null) return null;
          // Table CS: every entry in the book's order, the trashed too (a range's ends may sit in the trash).
          const order = orderChapters(source.entries.records).map((entry) => entry.id);
          return { metadata: serializeMetadata(read.book.metadata), theme: serializeTheme(read.book.theme), chapters: read.chapters, order };
        }),
      // Part 38e (tables WN and PN): the book's notes as stored, and the notes' own operations in the history's queue.
      notes: (bookId) =>
        run<readonly NoteRecord[]>([], async (device) => (await device.connection.store.readWhere('notes', 'bookId', bookId)).records),
      note: (id) => run<NoteRecord | undefined>(undefined, (device) => device.connection.store.read('notes', id).catch(() => undefined)),
      markShared: (id, share, mark) => run<NoteChangeResult>(NO_STORE, (device) => device.notes.markShared(id, share, mark)),
      applyPulled: (changes) => run<ApplyPulledResult>(NO_STORE, (device) => device.notes.applyPulled(changes)),
      notesStored: () =>
        run(undefined, async (device) => {
          await this.refresh(device);
        }),
    };
  }

  /**
   * Part 23b (V′5): the open book as a re-import reads it: as the export takes it (table N, `readForExport`: its theme,
   * metadata and live chapters in order, the open one as typed) with each chapter's ID and text. Null with no book open,
   * or when the book can't be read whole (N7, N9).
   */
  async reimportBook(): Promise<ExportedBook | null> {
    const result: { book: ExportedBook | null } = { book: null };
    await this.withDevice(async (device) => {
      const source = await this.exportSource(device);
      result.book = source === null ? null : await readForExport(source);
    });
    return result.book;
  }

  /**
   * Part 23b, CS2–CS5: the export base a re-imported chapter names: snapshot `snapshotId`, when it is a snapshot of chapter
   * `chapterId` of book `bookId` pinned by a DOCX export (decision 64); null for anything else (CS5: none, another
   * chapter's, another book's, not an export's, or unreadable). Its title (fix-next 27, table PT): the one its export pin
   * keeps, null when it keeps none.
   */
  async exportBase(bookId: string, chapterId: string, snapshotId: string): Promise<ExportBase | null> {
    const result: { base: ExportBase | null } = { base: null };
    await this.withDevice(async (device) => {
      const snapshot = await device.connection.store.read('snapshots', snapshotId).catch(() => undefined);
      const exports = snapshot?.pins.flatMap((pin) => (pin.kind === 'docx-export' ? [pin] : [])) ?? [];
      if (snapshot?.chapterId !== chapterId || snapshot.bookId !== bookId || exports.length === 0) return;
      const text = await device.history.text(snapshotId).catch(() => undefined);
      if (text === undefined) return;
      result.base = { text, title: exports.length === 1 ? (exports[0]?.title ?? null) : null };
    });
    return result.base;
  }

  /**
   * Part 23b, PI1, PI2, PI11: the image book `bookId` stores under `id`, by its record's hash (never a name's), its bytes
   * checked against it (part 16a's load); null when it holds none, or the record or bytes can't be read or don't match.
   */
  async storedImage(bookId: string, id: string): Promise<StoredPicture | null> {
    const result: { picture: StoredPicture | null } = { picture: null };
    await this.withDevice(async (device) => {
      const record = await device.connection.store.readImage(bookId, id).catch(() => undefined);
      if (record === undefined) return;
      const reference = { id, sha256: record.sha256 };
      const loaded = await this.library(device).load(bookId, reference).catch(() => null);
      if (loaded?.kind === 'image') result.picture = { reference, bytes: loaded.bytes };
    });
    return result.picture;
  }

  /** Part 23b, PI5: book `bookId`'s image whose bytes' SHA-256 is `sha256` and that loads, or null. */
  async imageByHash(bookId: string, sha256: string): Promise<ImageReference | null> {
    const result: { reference: ImageReference | null } = { reference: null };
    await this.withDevice(async (device) => {
      const record = await device.connection.store.findImage(bookId, sha256).catch(() => undefined);
      if (record === undefined) return;
      const reference = { id: record.id, sha256: record.sha256 };
      const loaded = await this.library(device).load(bookId, reference).catch(() => null);
      if (loaded?.kind === 'image') result.reference = reference;
    });
    return result.reference;
  }

  /**
   * Part 23b, table AP: a reviewed re-import written into the open book (`ChapterBook.reimport`): the open chapter saved
   * and closed first (AP6), then the snapshots and the one write; the open chapter opens again, whatever happened. Nothing
   * is written for another book than the open one, while the workspace can't be edited, or when the save fails.
   */
  reimport(request: ReimportRequest): Promise<ReimportCommitResult> {
    const result: { value: ReimportCommitResult } = { value: { kind: 'not-editable' } };
    return this.change(async (device) => {
      if (request.bookId !== this.bookId.get()) return;
      if (!(await this.closeEditor())) {
        result.value = { kind: 'save-failed' };
        return;
      }
      const open = this.openChapter.get();
      try {
        const written = await device.book.reimport(request.bookId, request.taken, request.added, request.commented);
        result.value =
          written.kind === 'reimported'
            ? { kind: 'reimported', changed: request.taken.length, added: written.added.length, copies: request.taken.filter((chapter) => chapter.copy).length, comments: written.comments }
            : { kind: written.kind };
        if (written.kind === 'reimported') this.textsChanged(request.bookId);
      } finally {
        await this.refresh(device);
        await this.load(device, open);
      }
    }).then(() => result.value);
  }

  /** Part 16a's image library over `device`'s store. */
  private library(device: Device): ImageLibrary {
    return new ImageLibrary({ store: device.connection.store, digest: this.options.digest, newId: this.options.newId });
  }

  /**
   * Part 15a, M24: a chapter's stored text, for the book view's shelf: empty when it has none, null when it can't be read
   * (or no store is open). The open chapter's typing isn't in it; the book view lays that chapter out from the editor.
   */
  async chapterText(chapterId: string): Promise<string | null> {
    const result: { text: string | null } = { text: null };
    await this.withDevice(async (device) => {
      const text = await readText(device.connection, chapterId);
      result.text = text === undefined ? '' : text;
    });
    return result.text;
  }

  /**
   * Part 19a, M5, M6: the texts a search reads, after everything queued before it: the open chapter (`chapter`) or every
   * live chapter of the open book in order (`book`), the open one as typed, the others as stored (none stored: empty);
   * one that can't be read is left out and counted. Null with no book open.
   */
  async findTexts(scope: FindScope): Promise<FindTexts | null> {
    const result: { texts: FindTexts | null } = { texts: null };
    await this.withDevice(async (device) => {
      if (this.bookId.get() === null) return;
      const slot = this.slot;
      const open = this.openChapter.get();
      const ids = scope === 'chapter' ? (open === null ? [] : [open]) : this.chapters().map((entry) => entry.id);
      const chapters = await readChapters(ids, (id) => (slot?.chapterId === id ? Promise.resolve(slot.editor.parsed.text) : readText(device.connection, id)));
      result.texts = { chapters, unreadable: ids.length - chapters.length };
    });
    return result.texts;
  }

  /**
   * Part 19a, table L (L3): replaces in the open chapter (`chapter`) or the whole open book (`book`). The open chapter is
   * saved and closed first (L1), the texts in scope read as stored (one that can't be read is left out), and `compute`
   * (the find worker) makes their new texts (L2); the chapters it changed are snapshotted and written in one write
   * (`ChapterBook.replaceAll`, L4–L7), and kept for the undo (U7). The open chapter opens again, whatever happened (L8).
   */
  replaceAll(scope: FindScope, compute: (chapters: readonly FindChapter[]) => Promise<ReplaceComputation>): Promise<ReplaceAllOutcome> {
    const result: { value: ReplaceAllOutcome } = { value: { kind: 'not-editable' } };
    return this.change(async (device) => {
      const bookId = this.bookId.get();
      if (bookId === null) return;
      if (!(await this.closeEditor())) {
        result.value = { kind: 'save-failed' };
        return;
      }
      const open = this.openChapter.get();
      try {
        result.value = await this.replaceIn(device, bookId, scope === 'chapter' ? (open === null ? [] : [open]) : this.chapters().map((entry) => entry.id), compute);
      } finally {
        await this.refresh(device);
        await this.load(device, open);
      }
    }).then(() => result.value);
  }

  /** L2–L7 for the chapters `ids` of `bookId`, the editor closed. */
  private async replaceIn(device: Device, bookId: string, ids: readonly string[], compute: (chapters: readonly FindChapter[]) => Promise<ReplaceComputation>): Promise<ReplaceAllOutcome> {
    const chapters = await readChapters(ids, (id) => readText(device.connection, id));
    const computed = await compute(chapters);
    if (computed.kind !== 'replaced') return computed;
    const changes: ReplacedText[] = [];
    let made = 0;
    let left = 0;
    computed.chapters.forEach((answer, i) => {
      const chapter = chapters[i];
      // An answer for no chapter sent is ignored; a chapter with no answer is unchanged.
      if (chapter === undefined) return;
      made += answer.made;
      left += answer.left;
      if (answer.text !== null) changes.push({ id: chapter.id, bookId, before: chapter.text, after: answer.text });
    });
    if (changes.length === 0) return { kind: 'replaced', made: 0, left, chapters: 0 };
    const written = await device.book.replaceAll(changes);
    if (written.kind !== 'replaced') return written.kind === 'failed' ? { kind: 'write-failed' } : written;
    this.lastReplace.set({ bookId, chapters: changes });
    this.textsChanged(bookId);
    return { kind: 'replaced', made, left, chapters: changes.length };
  }

  /**
   * Part 19a, table U: the book-wide undo of the open book's last replace-all: the open chapter saved and closed, each
   * chapter still holding what the replace wrote given its earlier text back (`ChapterBook.undoReplaceAll`), then the
   * open chapter opened again. Once done, there is nothing more to undo (U6).
   */
  undoReplaceAll(): Promise<UndoReplaceOutcome> {
    const result: { value: UndoReplaceOutcome } = { value: { kind: 'not-editable' } };
    return this.change(async (device) => {
      const last = this.lastReplace.get();
      if (last?.bookId !== this.bookId.get()) return;
      if (!(await this.closeEditor())) {
        result.value = { kind: 'save-failed' };
        return;
      }
      const open = this.openChapter.get();
      try {
        const undone = await device.book.undoReplaceAll(last.chapters);
        if (undone.kind === 'undone') {
          this.lastReplace.set(null);
          this.textsChanged(last.bookId);
          result.value = { kind: 'undone', restored: undone.restored.length, kept: undone.kept.length };
        } else result.value = undone.kind === 'failed' ? { kind: 'write-failed' } : undone;
      } finally {
        await this.refresh(device);
        await this.load(device, open);
      }
    }).then(() => result.value);
  }

  /** Part 24a, table N': the open book to bundle, once the open chapter's and the notes' typing have been saved; null with none open. */
  async bundleRequest(): Promise<BundleRequest | null> {
    const result: { request: BundleRequest | null } = { request: null };
    await this.withDevice(async (device) => {
      const slot = this.slot;
      result.request = await bundleRequest({
        book: this.book(),
        device: device.id,
        flush: slot === null ? null : () => slot.editor.flush(),
        // N'4 (part 19d): what is left in the drafts after the write never reached the store (failed, or the lock gone).
        flushNotes: async () => {
          await this.writeNotes(device);
          return this.drafts.size === 0;
        },
        untitled: cleanTitle(this.options.untitledBook()),
      });
      // Part 24d, H′1 (deep-fix r3): what the bundle hands over is pinned in this device's history, so an update from a
      // bundle made from it finds the version both last shared; one that can't be pinned never stops the backup.
      if (result.request !== null) await this.pinHandover(device, result.request.bookId);
    });
    return result.request;
  }

  /**
   * Part 24d, H′1, H′3: each chapter of book `bookId`, live or in the trash, pinned `bundle` for a year in this device's
   * history as stored now (none stored: empty, 24a's A8; one its reader refuses: left out), as a DOCX export's base is.
   */
  private async pinHandover(device: Device, bookId: string): Promise<void> {
    const { store } = device.connection;
    const { records } = await store.readWhere('entries', 'bookId', bookId);
    const chapters = [];
    for (const entry of records) {
      const text = await readText(device.connection, entry.id);
      if (text !== null) chapters.push({ id: entry.id, bookId, text: text ?? '' });
    }
    await device.history.pinExport(chapters, exportPinUntil(this.options.now()), 'bundle');
  }

  /** B12, B13: changes the open book's metadata, if its record can be read or there is none (B3). */
  private changeBook(change: (metadata: BookMetadata) => BookMetadata): Promise<void> {
    return this.change(async (device) => {
      const book = this.book();
      if (book === null) return;
      if (book.stored && book.metadata === null) {
        this.currentNotice.set({ kind: 'unreadable' });
        return;
      }
      let metadata: BookMetadata;
      try {
        metadata = parseMetadata(change(book.metadata ?? newMetadata(this.options.untitledBook())));
      } catch {
        this.currentNotice.set({ kind: 'title-refused' });
        return;
      }
      const { connection } = device;
      const existing = await connection.store.read('books', book.id).catch(() => undefined);
      const record: BookRecord = {
        id: book.id,
        metadata: serializeMetadata(metadata),
        theme: existing?.theme ?? serializeTheme(DEFAULT_THEME),
        createdAt: existing?.createdAt ?? this.options.now(),
      };
      this.report(await connection.session.write([{ kind: 'put', store: 'books', value: record }]));
      await this.loadBooks(connection);
      const slot = this.slot;
      if (slot !== null && book.metadata?.language !== metadata.language && (await this.closeEditor())) await this.load(device, slot.chapterId);
    });
  }

  /** The open book's live chapters, in order; none before the listing is read. */
  private chapters(): readonly ChapterEntry[] {
    return this.chapterListing.get()?.chapters ?? [];
  }

  /** The chapter of `bookId` to open: the one open last if it is still in the book, else the first (C6). */
  private lastChapter(bookId: string): string | null {
    const chapters = this.chapters();
    const remembered = this.deviceSettings.get().lastChapters[bookId];
    return chapters.find((entry) => entry.id === remembered)?.id ?? chapters[0]?.id ?? null;
  }

  /** Reads the open book's listing again (B-rows: every change ends with it). */
  private async refresh(device: Device): Promise<void> {
    const bookId = this.bookId.get();
    if (bookId === null) return;
    const listing = await device.book.list(bookId);
    if ('kind' in listing) {
      this.currentNotice.set({ kind: 'read-failed' });
      return;
    }
    // Every conflict copy of the book, not only those whose chapter is gone: typing kept from a chapter that is still
    // here is the writer's to see too (*deep-fix r1*). Copies the reader refused are in the listing's `invalid`.
    const { records } = await device.connection.store.readWhere('conflictCopies', 'bookId', bookId);
    const entries = [...listing.chapters, ...listing.trash];
    // Part 19c, K4; part 19e, NR7: the book's notes with it. Part 39, AC1: a base that couldn't be read is tried again.
    const notes = await device.notes.list(bookId);
    for (const [key, base] of this.noteBases) if (base === null) this.noteBases.delete(key);
    if ('kind' in notes) this.currentNotice.set({ kind: 'read-failed' });
    batch(() => {
      this.chapterListing.set(listing);
      if (!('kind' in notes)) this.noteListing.set(notes);
      this.keptCopies.set(records.map((copy) => ({ copy, chapter: entries.find((entry) => entry.id === copy.chapterId) ?? null })));
    });
    this.copyChanged(bookId);
    // Part 19e, AR7: the open chapter's notes found again as the listing now has them.
    this.findNotes();
  }

  /**
   * C1, C6, C7: opens `chapterId` in a new editor (none open now). A chapter with no stored text opens empty; one whose
   * text can't be read doesn't open, so nothing overwrites it. The chapter is remembered, its snapshots thinned (part
   * 07 notes), and the long-chapter check runs soon after (decision 14).
   */
  private async load(device: Device, chapterId: string | null): Promise<void> {
    const bookId = this.bookId.get();
    this.long.set(false);
    this.openChapter.set(chapterId);
    if (chapterId === null || bookId === null) return;
    const text = await readText(device.connection, chapterId);
    if (text === null) {
      this.currentNotice.set({ kind: 'unreadable' });
      return;
    }
    const state: SlotState = { chapterId, bookId, base: text ?? '', diverged: false, saving: null, connection: device.connection };
    const editor = this.options.openEditor({
      text: state.base,
      language: this.book()?.metadata?.language ?? 'en-US',
      autoConversion: this.deviceSettings.get().autoConversion,
      paragraphLook: this.paragraphLook(bookId),
      save: (value) => this.save(state, value),
      // A status the editor reports while it is made (E1: a text it normalised) is read again just below; a closed
      // editor reports nothing more (part 09, A13).
      onStatus: (status) => {
        this.onStatus(status);
      },
    });
    const slot: Slot = Object.assign(state, { editor });
    this.slot = slot;
    this.openEditor.set(editor);
    this.editorStatus.set(editor.status);
    editor.pause(!this.writable());
    this.deviceSettings.set(rememberChapter(this.deviceSettings.get(), bookId, chapterId));
    void this.storeSettings(device);
    void device.history.thinChapter(chapterId);
    this.options.soon(() => {
      if (this.slot !== slot) return;
      this.long.set(isLongChapter(slot.editor.parsed));
      // Part 19e, AR7: the chapter's notes found as it opens.
      this.findNotes();
    });
  }

  /**
   * W2–W4: saves an editor's text through the connection it now has: as the chapter through the session (which keeps
   * it as a conflict copy when this tab lost the lock), or straight to a new conflict copy once the chapter changed
   * under the editor.
   */
  private async save(slot: SlotState, text: string): Promise<AutosaveResult> {
    // Fix-next 1: set before the first wait, so a record kept in this task names the text this save may store.
    slot.saving = text;
    try {
      return await this.saveNow(slot, text);
    } finally {
      slot.saving = null;
    }
  }

  /** W2–W4: {@link save}'s write. */
  private async saveNow(slot: SlotState, text: string): Promise<AutosaveResult> {
    const { store, session } = slot.connection;
    if (slot.diverged) {
      const copy = { id: this.options.newId(), chapterId: slot.chapterId, bookId: slot.bookId, text, createdAt: this.options.now(), origin: 'stale-tab' } as const;
      const created = await store.create('conflictCopies', copy);
      if (created.kind !== 'created') return { kind: 'failed' };
      this.copyKept();
      return { kind: 'copied' };
    }
    const result = await session.save({ id: slot.chapterId, bookId: slot.bookId, text });
    if (result.kind === 'saved') slot.base = text;
    if (result.kind === 'copied') this.copyKept();
    return result.kind === 'copied' ? { kind: 'copied' } : result.kind === 'saved' ? { kind: 'saved' } : { kind: 'failed' };
  }

  /**
   * W3, W4: tells the writer typing was kept as a copy, and reads the listing again after whatever runs now, so the copy
   * shows under "Needs your attention" where the notice sends the writer (*deep-fix r3*).
   */
  private copyKept(): void {
    this.currentNotice.set({ kind: 'copy-kept' });
    void this.withDevice((device) => this.refresh(device));
  }

  /**
   * The open editor's status; after each save, an interval snapshot when one is due (decision 29; part 07 notes), and the
   * chapter's notes found again (part 19e, AR7: a save comes after a pause in typing, never on a keystroke).
   */
  private onStatus(status: AutosaveStatus): void {
    const previous = this.editorStatus.get();
    this.editorStatus.set(status);
    if (status === 'saved' && previous === 'saving') this.findNotes();
    // Part 38b, DI6: a save is a pause in typing, never a keystroke; the invites' copy waits for it.
    if (status === 'saved' && previous === 'saving' && this.slot !== null) for (const listener of this.saveListeners) listener(this.slot.bookId);
    // Fix-next 1, U7.
    this.settleUnsaved();
    // Decision 3: a failing autosave is said where focus mode can't hide it, until a save succeeds (*deep-fix r3*).
    if (status === 'failed') this.currentNotice.set({ kind: 'autosave-failed' });
    if (status === 'saved' && this.currentNotice.get()?.kind === 'autosave-failed') this.currentNotice.set(null);
    const slot = this.slot;
    // The device read now, on the connection the editor saves through: after a new connection (L5, L6) the history
    // the chapter opened with writes through a closed session (*deep-fix r1*).
    const device = this.device;
    if (status !== 'saved' || previous !== 'saving' || slot === null || slot.diverged || device?.connection !== slot.connection) return;
    void (async () => {
      if (!(await device.history.due(slot.chapterId, true))) return;
      await device.history.take({ id: slot.chapterId, bookId: slot.bookId, text: slot.base }, 'interval');
    })().catch(() => undefined);
  }

  /** Part 19c, N1: a field's stored text in the open book's listing (a chapter in the book or its trash, or a note). */
  private storedNote(target: NoteTarget): string | undefined {
    if (target.kind === 'note') return this.noteListing.get()?.notes.find((note) => note.id === target.id)?.[target.field];
    const listing = this.chapterListing.get();
    return [...(listing?.chapters ?? []), ...(listing?.trash ?? [])].find((entry) => entry.id === target.id)?.[target.field];
  }

  /**
   * Part 19e, AR1–AR8, MK1–MK4: finds the open chapter's notes in its text as the editor holds it now (each standing first
   * note anchored on it, within one scan budget), keeps where, and shows the open ones found as marks: Word's always, the
   * writer's while "Show private notes in text" is on. Runs off the keystroke path (AR7); with no chapter open, none.
   */
  private findNotes(): void {
    const slot = this.slot;
    const listing = this.noteListing.get();
    if (slot === null || listing === null) {
      this.foundNotes.set(null);
      return;
    }
    const places = new ChapterPlaces(slot.editor.parsed);
    const budget = new ScanBudget();
    const found = new Map<string, TextRange | null>();
    const marks: NoteMark[] = [];
    const showPrivate = this.deviceSettings.get().noteMarks;
    const alignments = new Map<string, Alignment>();
    for (const note of listing.notes) {
      if (note.chapterId !== slot.chapterId || note.anchor === null || note.deletedAt !== null || note.parentId !== null) continue;
      const range = places.locate(note.anchor, budget, this.alignmentFor(note, slot.chapterId, places, alignments));
      found.set(note.id, range);
      if (range === null || note.resolved || (note.origin === 'writer' && !showPrivate)) continue;
      const source = places.sourceOf(range);
      // MK; deep-fix r6 (part 38d): every note but the writer's is someone else's, a reader's too.
      marks.push({ from: source.from, to: source.to, kind: note.origin === 'writer' ? 'private' : 'editor' });
    }
    this.foundNotes.set({ chapterId: slot.chapterId, found });
    slot.editor.showNotes(marks);
  }

  /**
   * Part 19e, NO6, NO7, D′11 (C7; deep-fix r1): note `noteId` found in the open chapter's text as it is this moment (the
   * last find's offsets may be behind the typing since), with the places to read it by; undefined for a note that isn't a
   * standing anchored first note of the open chapter.
   */
  private locateNow(noteId: string): { readonly anchor: NoteAnchor; readonly places: ChapterPlaces; readonly range: TextRange | null } | undefined {
    const note = this.noteListing.get()?.notes.find((each) => each.id === noteId);
    const slot = this.slot;
    const anchor = note?.anchor ?? null;
    if (slot === null) return undefined;
    if (note === undefined || anchor === null || note.chapterId !== slot.chapterId || note.parentId !== null || note.deletedAt !== null) return undefined;
    const places = new ChapterPlaces(slot.editor.parsed);
    return { anchor, places, range: places.locate(anchor, new ScanBudget(), this.alignmentFor(note, slot.chapterId, places, new Map())) };
  }

  /**
   * Part 39, AC1: the alignment of the text a reader's note on chapter `chapterId` was made in (its link's pinned snapshot
   * of the chapter) with the chapter as `places` holds it, made once per base in `made`; null for any other note, or a
   * base not read (yet: the read is started, and the notes are found again once it is done).
   */
  private alignmentFor(note: NoteRecord, chapterId: string, places: ChapterPlaces, made: Map<string, Alignment>): Alignment | null {
    // AC9 (deep-fix r1): a note the writer has acted on (resolved or reopened) holds an anchor in the chapter's own text
    // as it stood then, no longer in the snapshot's: no base. A pull never changes a note's `editedAt`.
    if (note.link === undefined || note.editedAt !== note.storedAt) return null;
    const key = `${note.link.link} ${chapterId}`;
    const base = this.noteBases.get(key);
    if (base === undefined) this.readNoteBase(key, note.bookId, note.link.link, chapterId);
    if (base === undefined || base === null) return null;
    let alignment = made.get(key);
    if (alignment === undefined) {
      alignment = alignTexts(base, places.visible);
      made.set(key, alignment);
    }
    return alignment;
  }

  /**
   * Part 39, AC1: reads the base under `key` (link `link`'s record in book `bookId`, the snapshot it pinned for chapter
   * `chapterId`, that snapshot's text), off the keystroke path, one read at a time; a base that can't be read (no such
   * record or chapter in it, no text kept, or a read failing, which the writer is told as any read is)
   * is kept as none, until the next listing. Then finds
   * the notes again, which asks for the next base not read.
   */
  private readNoteBase(key: string, bookId: string, link: string, chapterId: string): void {
    if (this.basesLoading) return;
    this.basesLoading = true;
    // Marked none first: a read that fails leaves it none, never asked again in a loop.
    this.noteBases.set(key, null);
    void this.withDevice(async (device) => {
      const { records } = await device.connection.store.readWhere('shareLinks', 'bookId', bookId);
      const record = records.find((each) => each.id === link);
      const snapshotId = record?.snapshots[record.chapters.indexOf(chapterId)];
      if (snapshotId === undefined) return;
      const text = await device.history.text(snapshotId);
      if (text !== undefined) this.noteBases.set(key, ChapterPlaces.of(text).visible);
    }).finally(() => {
      this.basesLoading = false;
      this.findNotes();
    });
  }

  /**
   * Part 19e, NO6, NO7 (C7): note `noteId`'s anchor to write when the writer acts on it: where it is found now (a detached
   * one keeps its quote with no range); undefined when it isn't a standing anchored note of the open chapter.
   */
  private anchorNow(noteId: string): NoteAnchor | null | undefined {
    const now = this.locateNow(noteId);
    if (now === undefined) return undefined;
    return now.range === null ? { ...now.anchor, range: null } : now.places.placeAt(now.range);
  }

  /** Part 19e, NO11, NO12: what a note's change that didn't go through tells the writer; `stale` is the session's to show. */
  private noteFailed(result: { readonly kind: string }): void {
    if (result.kind === 'missing') this.currentNotice.set({ kind: 'note-gone' });
    if (result.kind === 'failed') this.currentNotice.set({ kind: 'note-failed' });
  }

  /** Part 19c, N9: stores the notes' typing, after any write of it already under way. */
  private writeNotes(device: Device): Promise<void> {
    // Never rejects: the store's failures come back as results (BookNotes.write), so the chain always goes on.
    const run = this.notesWriting.then(() => this.writeNotesNow(device));
    this.notesWriting = run;
    return run;
  }

  /**
   * Part 19c, tables N and W: writes every field typed in, as it is now, in one write. A field typed in again during the
   * write stays, its base now what was written (N13); one gone (W1) is dropped. The listings take what was stored at once, so
   * typing that starts next has it as its base. Nothing written (another tab has the lock, the store failing) keeps it all.
   */
  private async writeNotesNow(device: Device): Promise<void> {
    const edits = [...this.drafts.values()];
    if (edits.length === 0) return;
    // Deep-fix r2: a record kept while this write runs names what it may store.
    for (const edit of edits) this.notesSaving.set(targetKey(edit.target), edit.value);
    const anchors = new Map<string, NoteAnchor | null>();
    for (const edit of edits) {
      const anchor = edit.target.kind === 'note' ? this.anchorNow(edit.target.id) : undefined;
      if (anchor !== undefined && edit.target.kind === 'note') anchors.set(edit.target.id, anchor);
    }
    const result = await device.notes.write(edits, anchors).finally(() => {
      for (const edit of edits) this.notesSaving.delete(targetKey(edit.target));
    });
    if (result.kind === 'stale') return;
    this.notesFailing = result.kind === 'failed';
    if (result.kind === 'failed') {
      this.currentNotice.set({ kind: 'notes-failed' });
      return;
    }
    for (const edit of edits) {
      const key = targetKey(edit.target);
      const now = this.drafts.get(key);
      const stored = result.stored.get(key);
      if (now === undefined) continue;
      if (stored === undefined || now.value === edit.value) this.drafts.delete(key);
      // N13 (deep-fix r1): typed in during the write: its base is what was written when that is what the field held; after
      // a join (W5) the field never showed the joined text, so the base stays, and the next write keeps both again.
      else if (stored === edit.value) this.drafts.set(key, { ...now, base: stored });
    }
    this.notesChanged((this.noteListing.get()?.notes ?? []).filter((note) => edits.some((edit) => edit.target.kind === 'note' && edit.target.id === note.id)));
    this.patchNotes(result.stored);
    this.noteOutcome(result.lost, result.joined);
    // Fix-next 1, U7.
    this.settleUnsaved();
  }

  /** Part 19c, N7, W1, W5: what a notes' write that went through tells the writer. */
  private noteOutcome(lost: number, joined: number): void {
    if (lost > 0) this.currentNotice.set({ kind: 'notes-lost' });
    else if (joined > 0) this.currentNotice.set({ kind: 'notes-joined' });
    else if (this.currentNotice.get()?.kind === 'notes-failed') this.currentNotice.set(null);
  }

  /** Fix-next 1, table U: whether the open chapter holds typing the store may not have (a save due, running or failed). */
  private typingUnsaved(): boolean {
    const status = this.slot?.editor.status;
    return status === 'unsaved' || status === 'saving' || status === 'failed';
  }

  /**
   * Fix-next 1, table U: this page's typing not stored yet: the open chapter's (its text, whether it diverged, the hash
   * of the text the store certainly held from it, and of the save running, kept apart: *deep-fix r1*), and the notes'
   * drafts; null with none.
   */
  private unsavedNow(): Unsaved | null {
    const slot = this.slot;
    let chapter: UnsavedChapter | null = null;
    if (slot !== null && this.typingUnsaved()) {
      const saving = slot.saving === null ? null : textHash(slot.saving);
      chapter = { chapterId: slot.chapterId, bookId: slot.bookId, text: slot.editor.parsed.text, base: textHash(slot.base), saving, diverged: slot.diverged };
    }
    const notes: UnsavedNote[] = [...this.drafts.values()].map((draft) => ({ ...draft, saving: this.notesSaving.get(targetKey(draft.target)) ?? null }));
    // Part 19e, CK1, CK2: the composers' words.
    // Only a key the record reads back is written: one it refused would make the whole record damaged (M4).
    const composers = [...this.composers.get()].flatMap(([key, { bookId, text }]) => (COMPOSER_KEY.test(key) ? [{ key, bookId, text }] : []));
    return chapter === null && notes.length === 0 && composers.length === 0 ? null : { chapter, notes, composers };
  }

  /**
   * Fix-next 1, U1–U5, U9: writes this page's record of the typing not stored yet, in this task, or removes it when there
   * is none. False when there is such typing and the record couldn't be written (U4: no storage, refused, full).
   */
  private keepUnsaved(): boolean {
    const unsaved = this.unsavedNow();
    if (unsaved === null) {
      this.dropUnsaved();
      return true;
    }
    const value = serializeUnsaved(unsaved);
    if (value === this.keptUnsaved) return true;
    try {
      this.unsavedKey ??= this.unsavedPrefix() + this.options.newId();
      this.options.unsavedStorage().setItem(this.unsavedKey, value);
      this.keptUnsaved = value;
      return true;
    } catch {
      return false;
    }
  }

  /** BK11: the prefix of this store's records of typing not stored yet. */
  private unsavedPrefix(): string {
    return this.options.space?.().unsaved ?? UNSAVED_PREFIX;
  }

  /** Fix-next 1, U2, U6: removes this page's record, if it wrote one; one that can't be removed is merged harmlessly (principle 2). */
  private dropUnsaved(): void {
    if (this.keptUnsaved === null || this.unsavedKey === null) return;
    try {
      this.options.unsavedStorage().removeItem(this.unsavedKey);
      this.keptUnsaved = null;
    } catch {
      // Kept as written: removed at the next chance, or merged by K1 once its typing is stored.
    }
  }

  /** Fix-next 1, U7, U8: once a save or a notes' write leaves nothing unsaved, this page's record goes. */
  private settleUnsaved(): void {
    // Part 19e, CK5: written again rather than dropped, so a composer's words in it stay (none: dropped as before).
    if (this.keptUnsaved !== null && !this.typingUnsaved() && this.drafts.size === 0) this.keepUnsaved();
  }

  /**
   * Fix-next 1, table M: merges every other page's record into the store (tables K and W), one at a time in key order
   * (M8), skipping this page's own (M5). Storage that can't be listed merges nothing (M6); a record the store throws on or
   * fails to write is kept (M3, M7), and the writer is told it waits (*deep-fix r2*). Never throws.
   */
  private async recoverUnsaved(device: Device): Promise<void> {
    const keys: string[] = [];
    try {
      const storage = this.options.unsavedStorage();
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index);
        // BK11: only this store's records: another account's typing waits for its own store.
        if (key?.startsWith(this.unsavedPrefix()) === true && key !== this.unsavedKey) keys.push(key);
      }
    } catch {
      return;
    }
    let waiting = false;
    for (const key of keys.sort()) {
      try {
        if ((await this.recoverRecord(device, key)) === 'failed') waiting = true;
      } catch {
        // M7: kept, merged next turn.
        waiting = true;
      }
    }
    if (waiting) this.currentNotice.set({ kind: 'unsaved-waiting' });
  }

  /**
   * Fix-next 1, M1–M4: merges one page's record; it goes once every part is in the store. `failed` when a write failed
   * (M3: the writer is told), `stale` when the lock went (the session shows that), else `done`.
   */
  private async recoverRecord(device: Device, key: string): Promise<'done' | 'stale' | 'failed'> {
    const storage = this.options.unsavedStorage();
    const raw = storage.getItem(key);
    if (raw === null) return 'done';
    const read = readUnsaved(raw);
    if (read.kind !== 'read') return 'done';
    const { chapter, notes, composers } = read.unsaved;
    if (chapter !== null) {
      const written = await this.recoverChapter(device, chapter);
      if (written !== 'done') return written;
      // M2: a copy made is never made again.
      if (notes.length > 0 || composers.length > 0) storage.setItem(key, serializeUnsaved({ chapter: null, notes, composers }));
    }
    if (notes.length > 0) {
      const edits: NoteEdit[] = [];
      for (const note of notes) edits.push(noteEditOf(note, await this.storedField(device, note)));
      const result = await device.notes.write(edits);
      if (result.kind !== 'written') return result.kind;
      this.noteOutcome(result.lost, result.joined);
      if (composers.length > 0) storage.setItem(key, serializeUnsaved({ chapter: null, notes: [], composers }));
    }
    // CK8: the source goes only once this page's own record holds the words.
    if (composers.length > 0 && !(await this.recoverComposers(device, composers))) return 'failed';
    storage.removeItem(key);
    return 'done';
  }

  /**
   * Part 19e, deep-fix r1, CK6–CK8: another page's composers' words into this page's composers, never a note: each to its
   * own composer while its chapter or thread stands in its book (beside words already there), else the book's Notebook
   * composer; a book gone takes its words with it. False, with nothing taken, when this page's record can't hold them.
   */
  private async recoverComposers(device: Device, composers: readonly UnsavedComposer[]): Promise<boolean> {
    const { store } = device.connection;
    const before = this.composers.get();
    const next = new Map(before);
    for (const { key, bookId, text } of composers) {
      if ((await store.read('books', bookId)) === undefined) continue;
      const into = (await this.composerStands(device, key, bookId)) ? key : `notebook:${bookId}`;
      const there = next.get(into)?.text ?? '';
      next.set(into, { bookId, text: there === '' || there === text ? text : `${there}\n\n${text}` });
    }
    this.composers.set(next);
    if (this.keepUnsaved()) return true;
    this.composers.set(before);
    return false;
  }

  /** CK6, CK7: whether composer `key`'s chapter or thread stands in book `bookId`; one refused reads as gone. */
  private async composerStands(device: Device, key: string, bookId: string): Promise<boolean> {
    const [kind, id = ''] = key.split(':');
    const { store } = device.connection;
    try {
      if (kind === 'chapter') {
        // A chapter in the trash shows no composer: its words go to the Notebook's (CK7, deep-fix r2).
        const entry = await store.read('entries', id);
        return entry?.bookId === bookId && entry.trashed === null;
      }
      if (kind === 'reply') {
        // Deep-fix r8: a reply composer shows only under a standing first note of a chapter out of the trash (This
        // chapter's threads); a Notebook note's, or one under a trashed chapter, has no screen.
        const parent = await store.read('notes', id);
        if (parent?.bookId !== bookId || parent.deletedAt !== null || parent.parentId !== null || parent.chapterId === null) return false;
        const entry = await store.read('entries', parent.chapterId);
        return entry?.bookId === bookId && entry.trashed === null;
      }
    } catch {
      return false;
    }
    return true;
  }

  /** Deep-fix r2: a note field's stored text, read afresh (undefined when its record is gone); a record refused throws (M7). */
  private async storedField(device: Device, { target }: NoteEdit): Promise<string | undefined> {
    const { store } = device.connection;
    if (target.kind === 'chapter') return (await store.read('entries', target.id))?.[target.field];
    // Part 19e, MU2: an earlier build's typing in a chapter's notes, whose note the move never made (they were empty):
    // the chapter's whole-chapter note made under its ID first, while the chapter is stored.
    if ((await store.read('notes', target.id)) === undefined && (await store.read('entries', target.id)) !== undefined) {
      const ensured = await device.notes.ensureChapterNote(target.id);
      if (ensured.kind !== 'done' && ensured.kind !== 'missing') throw new Error(ensured.kind);
    }
    return (await store.read('notes', target.id))?.[target.field];
  }

  /** Fix-next 1, table K: one record's chapter part into the store: `done` once it is there (or needs nothing). */
  private async recoverChapter(device: Device, chapter: UnsavedChapter): Promise<'done' | 'stale' | 'failed'> {
    const { store, session } = device.connection;
    const stored = await readText(device.connection, chapter.chapterId);
    // An entry that can't be read isn't live: the typing goes to a copy rather than over a chapter nobody can see.
    const entry = await store.read('entries', chapter.chapterId).catch(() => undefined);
    const live = entry?.trashed === null && entry.bookId === chapter.bookId;
    const recovery = recoveryOf(chapter, stored, live);
    if (recovery === 'none') return 'done';
    if (recovery === 'chapter') {
      const written = await session.write([{ kind: 'put', store: 'chapters', value: { id: chapter.chapterId, bookId: chapter.bookId, text: chapter.text } }]);
      return written.kind === 'committed' ? 'done' : written.kind;
    }
    const copy = { id: this.options.newId(), chapterId: chapter.chapterId, bookId: chapter.bookId, text: chapter.text, createdAt: this.options.now(), origin: 'stale-tab' } as const;
    const written = await session.write([{ kind: 'add', store: 'conflictCopies', value: copy }]);
    if (written.kind !== 'committed') return written.kind;
    this.currentNotice.set({ kind: 'copy-kept' });
    return 'done';
  }

  /** Part 19c, N9: the listings with the notes' fields as just stored. */
  private patchNotes(stored: ReadonlyMap<string, string>): void {
    if (stored.size === 0) return;
    const entry = (each: ChapterEntry): ChapterEntry => ({
      ...each,
      synopsis: stored.get(targetKey({ kind: 'chapter', id: each.id, field: 'synopsis' })) ?? each.synopsis,
    });
    const field = (note: NoteRecord, name: 'text' | 'title' | 'group'): string => stored.get(targetKey({ kind: 'note', id: note.id, field: name })) ?? note[name];
    const listing = this.chapterListing.get();
    const notes = this.noteListing.get();
    batch(() => {
      if (listing !== null) this.chapterListing.set({ ...listing, chapters: listing.chapters.map(entry), trash: listing.trash.map(entry) });
      if (notes !== null) {
        this.noteListing.set({
          ...notes,
          notes: notes.notes.map((note) => ({ ...note, text: field(note, 'text'), title: field(note, 'title'), group: field(note, 'group') })),
        });
      }
    });
  }

  /**
   * C8: saves and closes the open editor. False when its save failed: the editor stays open with its text, and the
   * writer is told; whatever was asked doesn't happen (tenet 3).
   */
  private async closeEditor(): Promise<boolean> {
    const slot = this.slot;
    if (slot === null) return true;
    const result = await slot.editor.close();
    if (result.kind === 'failed') {
      this.currentNotice.set({ kind: 'save-failed' });
      return false;
    }
    // Whatever runs next opens a chapter or none (C1–C5), which sets the open chapter and the long-chapter flag.
    this.slot = null;
    this.editorStatus.set(null);
    this.openEditor.set(null);
    return true;
  }

  /** Stores the device's settings; a failure keeps them in memory and tells nobody (comfort only, never text). The session's write never rejects. */
  private async storeSettings(device: Device): Promise<void> {
    const value = { id: device.id, settings: serializeSettings(this.deviceSettings.get()) };
    await device.connection.session.write([{ kind: 'put', store: 'device', value }]);
  }

  /** W5: tells the writer about a change that couldn't be written; `stale` is the session's to show. */
  private report(result: { readonly kind: string }): void {
    if (result.kind === 'failed') this.currentNotice.set({ kind: 'write-failed' });
  }
}

/** Part 18, V11: why a new book wasn't stored: this tab lost the lock (`stale`), or anything else (`failed`). */
export function notCreated(result: { readonly kind: 'exists' | 'stale' | 'failed' }): CommitResult {
  return { kind: result.kind === 'stale' ? 'stale' : 'failed' };
}

/** A book record's metadata, or null when it can't be read (B2). */
/** The record's theme, or the default theme when it can't be read (part 15: the preview always has one to lay out with). */
function themeOf(record: BookRecord): Theme {
  try {
    return themeFromJson(record.theme);
  } catch {
    return DEFAULT_THEME;
  }
}

function metadataOf(record: BookRecord): BookMetadata | null {
  try {
    return metadataFromJson(record.metadata);
  } catch {
    return null;
  }
}

/** Part 19a, M5, M6: the chapters `ids` with their texts as `read` gives them, none stored read as empty, unreadable ones left out. */
async function readChapters(ids: readonly string[], read: (id: string) => Promise<string | null | undefined>): Promise<FindChapter[]> {
  const texts = await Promise.all(ids.map(async (id) => ({ id, text: await read(id) })));
  return texts.flatMap(({ id, text }) => (text === null ? [] : [{ id, text: text ?? '' }]));
}

/** A chapter's stored text; undefined when it has none, and null when it can't be read (C7). */
async function readText(connection: Connection, chapterId: string): Promise<string | null | undefined> {
  try {
    return (await connection.store.read('chapters', chapterId))?.text;
  } catch {
    return null;
  }
}
